import { PaletteMode } from '@mui/material';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { Feature } from 'geojson';
import { LngLatBoundsLike } from 'react-map-gl/mapbox';

import { BASEMAPS, DEFAULT_LOCATION_FILTER_CATEGORIES } from '@const';
import { RootState } from './store';
import { getAudienceTravelDateRange } from './utils/audienceTravelDateRange';
import {
  AdsbDetailsTab,
  AdsbFetchedData,
  AdsbFlightExt,
  AisFetchedData,
  Audience,
  AUDIENCE_ENTITY_TYPE,
  AUDIENCE_ENTITY_TYPE_OPTIONS,
  AudienceDetailsTab,
  AudienceEntityTypeFilter,
  Basemap,
  BurstFeature,
  BurstFeatureCollection,
  CampaignDwellAggDimKeys,
  CampaignRawAggDimensions,
  DataminrAlertFeature,
  DataminrAlertFeatureCollection,
  DataminrSortField,
  DataminrSortOrder,
  DrawTool,
  EGEDataRequestFilters,
  EGELayerRequest,
  EntityFlightEvidenceType,
  EntityFlightJourney,
  EntityFlightMilitaryType,
  EntityVesselEvidenceType,
  EntityVesselJourney,
  GetAudienceSort,
  GetCampaignResponse,
  getEntityFlightJourneyId,
  getEntityVesselJourneyId,
  GetLocationSort,
  GetLocationsResponse,
  GetWifiObservationsSurveyedOrConnectedResponse,
  GetWifiSort,
  MapViewport,
  PolygonMode,
  ProductTab,
  SCALE_TYPE,
  SenpaiSortField,
  SenpaiSortOrder,
  User,
  WIFIRow
} from './types';
import { GadmL0ResponseRow } from './types/gadm';
import { combineWktGeometries, getColorFromAdvertiserId, getDailySessionId, getWifiCompositeKey, mapBoundsWKT } from './utils';

export const SENPAI_FILTER_MIN_RFI_DEFAULT = 1;
/** No alert_types filter: the API returns every severity. */
export const DATAMINR_FILTER_ALERT_TYPES_DEFAULT: DataminrAlertFeature['properties']['alert_type'][] = [];

export interface DrawnEgePolygon {
  wkt: string;
  visible: boolean;
}

export interface SliceState {
  sessionId: string;
  user: User;
  activePage: string;
  campaign?: GetCampaignResponse | null;
  shareCampaign?: GetCampaignResponse | null;
  viewSourceCampaign?: GetCampaignResponse | null;
  campaignRawAggDimension: CampaignRawAggDimensions;
  campaignRawAggSize: number;
  eventCountColorOverride?: string;
  campaignOnScreenEntities?: string[];
  campaignTimelineRange: string;
  campaignSearchText: string;
  egeRawDateRange?: [number, number];
  egeRawPlaying: boolean;
  isPanelHeaderVisible: boolean;
  panelHasBeenManuallyClosed: boolean; // Track if user has manually closed the panel

  campaignClusterDateRange?: [number, number];

  campaignH3DwellLayerEnabled: boolean;
  campaignH3DwellLayerDate?: [number, number];
  campaignH3DwellLayerRes: number;
  campaignH3DwellLayerDim: CampaignDwellAggDimKeys;
  campaignH3DwellLayerScaleFilter: number[];
  campaignH3DwellLayerScaleType: SCALE_TYPE;

  campaignFilterEndDate?: number | null;
  campaignFilterTouchstoneStartDate: number | null;
  campaignFilterTouchstoneEndDate?: number | null;
  campaignFilterEdgeStartDate?: number | null;
  campaignFilterEdgeEndDate?: number | null;
  campaignFilterContinuousCampaign: boolean;
  campaignFilterMinScore: number;
  campaignFilterShowTouchstoneChips: boolean;

  // Map State
  initialMapViewport: MapViewport;
  delayedMapViewport?: MapViewport;
  delayedMapBounds?: [[number, number], [number, number]];
  delayedMapBoundsWKT?: string;
  delayedMapBoundsBox?: {
    northEast: { lat: number; lng: number };
    southWest: { lat: number; lng: number };
  };
  mapCoordinateInfo?: {
    mgrs?: string;
    latitude: number;
    longitude: number;
    zoom: number;
  };

  selectedBasemap: Basemap;

  // Map Actions
  flyToViewport?: MapViewport | null;
  fitBounds?: null | LngLatBoundsLike;
  fitBoundsMarker?: [number, number] | null;
  fitBoundsImmediately: boolean;
  fitBoundsMaxZoom?: number;
  drawToolSelected: DrawTool;
  drawToolActive: boolean;
  drawnBboxBounds?: string; // WKT polygon of the drawn bounding box
  drawnEgePolygons: DrawnEgePolygon[]; // Individual polygons drawn for the EGE fetch "Draw" tool
  measurementActive: boolean;

  // UI Layout
  themeMode: PaletteMode | 'system';
  campaignCreatorOpen: boolean;
  isPanelCampaignCreatorOpen: boolean;
  isPanelFetchOpen: boolean;
  isPanelFetchCollapsed: boolean;
  panelFetchDataset: string;

  // Data Layer Toggles
  isEgeDataOn: boolean;
  polygonMode: PolygonMode;
  isWIFIDataOn: boolean;
  isCellDataOn: boolean;
  senpaiDataOn: boolean;
  egeLayerRequests: EGELayerRequest[];
  isAdsbDataOn: boolean;
  adsbFlights: AdsbFlightExt[];
  adsbFetchedData: AdsbFetchedData[];
  aisFetchedData: AisFetchedData[];
  fetchDateRange: [number, number] | null; // Fetch data timeline filter (AdTech fetch and AIS layers), epoch ms [start, end); null is no filter
  showFetchDataTimeline: boolean; // Fetch data timeline shown in the bottom bar when there is fetched data
  nextFetchLayerNumber: number; // shared counter across EGE, ADS-B and AIS manual fetch layers

  // Searching
  searchBarFilter: string;
  detailPanelActiveTab: ProductTab;

  // Locations
  detailsLocation?: GetLocationsResponse;
  detailsLocationIdx: number;
  detailsLocationActiveTab: 'touchstone' | 'profile' | 'units' | 'notes';
  selectedLocations: GetLocationsResponse[];
  selectedLocationIds: string[];
  selectedLocationsLookup: Record<string, GetLocationsResponse>;
  selectedLocationAttrPanel?: string;
  sortLocationsBy: GetLocationSort;
  locationSortOrder: 'asc' | 'desc';
  locationFilterCountry: GadmL0ResponseRow[];
  locationFilterCategory: string[];
  locationFilterMinUniqueEntities: number;
  locationFilterAdvertiserIds: string[];
  locationShowOnlySelections: boolean;
  isLocationNetworkEnabled: boolean;
  locationNetworkPolygonId?: string;

  // Audience
  detailsAudience?: Audience;
  detailsAudienceIdx: number;
  detailsAudienceDateRange?: [number, number];
  detailsAudienceActiveTab: AudienceDetailsTab;
  detailsAdsbActiveTab: AdsbDetailsTab;
  adsbDetailCollapsed: boolean;
  selectedAudience: Audience[];
  selectedAudienceIds: string[];
  selectedAudienceLookup: Record<string, Audience>;
  selectedAudienceColorOverride: Record<string, string>;
  campaignEntityColors: Record<string, string>;
  sortAudienceBy: GetAudienceSort;
  audienceSortOrder: 'asc' | 'desc';
  audienceFilterMinPolygons: number;
  audienceFilterMinScore: number;
  audienceFilterEntityTypes: AudienceEntityTypeFilter[];
  audienceFilterFlightEvidenceTypes: EntityFlightEvidenceType[];
  audienceFilterFlightMilitaryTypes: EntityFlightMilitaryType[];
  audienceFilterVesselEvidenceTypes: EntityVesselEvidenceType[];
  selectedEntityFlightJourneys: EntityFlightJourney[];
  selectedEntityVesselJourneys: EntityVesselJourney[];
  // Hex colors picked for individual flight journeys, keyed by journey id
  entityFlightJourneyColors: Record<string, string>;
  audienceShowOnlySelections: boolean;

  // Senpai
  detailsSenpai: null | BurstFeature;
  selectedSenpaiList: BurstFeature[];
  selectedSenpaiLookup: Record<string, BurstFeature>;
  detailsSenpaiIdx: number;
  detailsSenpaiActiveTab: 'burst' | 'notes';
  senpaiShowOnlySelections: boolean;
  senpaiFilterMinRfiCount: number;
  senpaiSortField: SenpaiSortField;
  senpaiSortOrder: SenpaiSortOrder;

  // WIFI
  detailsWifi?: WIFIRow;
  hoveredWifi?: WIFIRow;
  detailsWifiIdx: number;
  selectedWifi: WIFIRow[];
  selectedWifiLookup: Record<string, WIFIRow>;
  wifiResults: WIFIRow[];
  wifiResultsLoading: boolean;
  wifiTotalCount: number;
  wifiReturnedCount: number;
  lastWifiQueryParams?: Record<string, unknown>; // cache last WiFi query params
  wifiShowOnlySelections: boolean;
  sortWifiBy?: GetWifiSort;
  wifiSortOrder: 'asc' | 'desc';
  wifiConnectedOrSurveyedData?: GetWifiObservationsSurveyedOrConnectedResponse[];
  wifiConnectedOrSurveyedLoading: boolean;

  // Banner state computed from WiFi data
  bannerHasFTPOClassification: boolean;

  // ARI
  ariData?: BurstFeatureCollection;
  ariIsLoading: boolean;
  ariIsError: boolean;

  // Dataminr
  dataminrDataOn: boolean;
  dataminrData?: DataminrAlertFeatureCollection;
  dataminrIsLoading: boolean;
  dataminrIsError: boolean;
  detailsDataminr: null | DataminrAlertFeature;
  selectedDataminrList: DataminrAlertFeature[];
  selectedDataminrLookup: Record<string, DataminrAlertFeature>;
  detailsDataminrIdx: number;
  detailsDataminrActiveTab: 'details' | 'notes';
  dataminrDetailCollapsed: boolean;
  dataminrShowOnlySelections: boolean;
  dataminrFilterAlertTypes: DataminrAlertFeature['properties']['alert_type'][];
  dataminrSortField: DataminrSortField;
  dataminrSortOrder: DataminrSortOrder;

  // Tacklebox integration
  tackleboxAdminLayer: string | null;
  tackleboxDrawnPolygonSelected?: string;
  tackleboxDrawModeEnabled: boolean;
  tackleboxJobName: string;
  tackleboxSlots: { id: number; polygon?: Feature }[];
  tackleboxDrawingSlotId: number | null;
  tackleboxMinTitoPolygons: number;
  tackleboxLoadedJobId?: string;
  tackleboxHistoryTab: 'mine' | 'shared';
}

const initialState: SliceState = {
  sessionId: getDailySessionId(),
  user: { email: '' },
  activePage: '/',
  themeMode: 'system',
  campaignRawAggDimension: 'count',
  campaignRawAggSize: 1,
  eventCountColorOverride: undefined,
  campaignH3DwellLayerEnabled: false,
  campaignH3DwellLayerRes: 7,
  campaignH3DwellLayerDim: CampaignDwellAggDimKeys.COUNT,
  campaignH3DwellLayerScaleFilter: [],
  campaignH3DwellLayerScaleType: 'Quantile',
  campaignTimelineRange: 'All',
  campaignSearchText: '',
  isPanelHeaderVisible: true,
  panelHasBeenManuallyClosed: false,

  // the map will load the viewport from `delayedMapViewport` if it is set,
  // otherwise it will load from `initialMapViewport`:
  initialMapViewport: {
    latitude: 30,
    longitude: 0,
    zoom: 2
  },
  flyToViewport: null,
  fitBoundsImmediately: false,
  isEgeDataOn: true,
  polygonMode: 'global' as const,
  isWIFIDataOn: false,
  isCellDataOn: false,
  senpaiDataOn: false,
  egeLayerRequests: [],
  isAdsbDataOn: AUDIENCE_ENTITY_TYPE_OPTIONS.includes(AUDIENCE_ENTITY_TYPE.adsb),
  adsbFlights: [],
  adsbFetchedData: [],
  aisFetchedData: [],
  fetchDateRange: null,
  showFetchDataTimeline: true,
  nextFetchLayerNumber: 1,

  selectedBasemap: BASEMAPS[0],
  egeRawPlaying: false,

  searchBarFilter: '',
  detailPanelActiveTab: 'touchstone',
  drawToolSelected: 'fetch-ege',
  drawToolActive: false,
  drawnEgePolygons: [],
  measurementActive: false,

  detailsLocation: undefined,
  detailsLocationIdx: 0,
  detailsLocationActiveTab: 'touchstone',
  detailsAudience: undefined,
  detailsAudienceIdx: 0,
  detailsAudienceDateRange: undefined,
  detailsAudienceActiveTab: 'attributes',
  detailsAdsbActiveTab: 'details',
  adsbDetailCollapsed: false,
  campaignCreatorOpen: false,
  isPanelCampaignCreatorOpen: false,
  isPanelFetchOpen: false,
  isPanelFetchCollapsed: false,
  panelFetchDataset: '',

  detailsWifi: undefined,
  hoveredWifi: undefined,
  detailsWifiIdx: -1,
  selectedWifi: [],
  selectedWifiLookup: {},
  wifiResults: [],
  wifiResultsLoading: false,
  wifiTotalCount: 0,
  wifiReturnedCount: 0,
  lastWifiQueryParams: undefined,
  wifiShowOnlySelections: false,
  sortWifiBy: 'observation_count',
  wifiSortOrder: 'desc',
  wifiConnectedOrSurveyedData: undefined,
  wifiConnectedOrSurveyedLoading: false,
  bannerHasFTPOClassification: false,

  // ARI
  ariData: undefined,
  ariIsLoading: false,
  ariIsError: false,

  // Dataminr
  dataminrDataOn: false,
  dataminrData: undefined,
  dataminrIsLoading: false,
  dataminrIsError: false,
  detailsDataminr: null,
  selectedDataminrList: [],
  selectedDataminrLookup: {},
  detailsDataminrIdx: -1,
  detailsDataminrActiveTab: 'details',
  dataminrDetailCollapsed: false,
  dataminrShowOnlySelections: false,
  dataminrFilterAlertTypes: DATAMINR_FILTER_ALERT_TYPES_DEFAULT,
  dataminrSortField: 'timestamp',
  dataminrSortOrder: 'desc',

  // Tacklebox integration
  tackleboxAdminLayer: null,
  tackleboxDrawnPolygonSelected: undefined,
  tackleboxDrawModeEnabled: false,
  tackleboxJobName: '',
  tackleboxSlots: [{ id: 0 }, { id: 1 }],
  tackleboxDrawingSlotId: null,
  tackleboxMinTitoPolygons: 2,
  tackleboxLoadedJobId: undefined,
  tackleboxHistoryTab: 'mine',

  selectedLocations: [],
  selectedLocationIds: [],
  selectedLocationsLookup: {},
  sortLocationsBy: 'unique_visitors',
  locationSortOrder: 'desc',
  locationFilterCountry: [],
  locationFilterCategory: [...DEFAULT_LOCATION_FILTER_CATEGORIES],
  locationFilterMinUniqueEntities: 0,
  locationFilterAdvertiserIds: [],
  locationShowOnlySelections: false,
  isLocationNetworkEnabled: false,

  selectedAudience: [],
  selectedAudienceIds: [],
  selectedAudienceLookup: {},
  selectedAudienceColorOverride: {},
  campaignEntityColors: {},
  sortAudienceBy: 'polygons-all-time',
  audienceSortOrder: 'desc',
  audienceFilterMinPolygons: 0,
  audienceFilterMinScore: 0,
  audienceFilterEntityTypes: [...AUDIENCE_ENTITY_TYPE_OPTIONS],
  audienceFilterFlightEvidenceTypes: [],
  audienceFilterFlightMilitaryTypes: [],
  audienceFilterVesselEvidenceTypes: [],
  selectedEntityFlightJourneys: [],
  selectedEntityVesselJourneys: [],
  entityFlightJourneyColors: {},
  audienceShowOnlySelections: false,

  detailsSenpai: null,
  selectedSenpaiList: [],
  selectedSenpaiLookup: {},
  detailsSenpaiIdx: -1,
  detailsSenpaiActiveTab: 'burst',
  senpaiShowOnlySelections: false,
  senpaiFilterMinRfiCount: SENPAI_FILTER_MIN_RFI_DEFAULT,
  senpaiSortField: 'rfi_count',
  senpaiSortOrder: 'desc',
  campaignFilterContinuousCampaign: false,
  campaignFilterTouchstoneStartDate: dayjs().subtract(90, 'day').startOf('day').valueOf(),
  campaignFilterTouchstoneEndDate: dayjs().endOf('day').valueOf(),
  campaignFilterEdgeStartDate: dayjs().subtract(90, 'day').startOf('day').valueOf(),
  campaignFilterEdgeEndDate: dayjs().endOf('day').valueOf(),
  campaignFilterMinScore: 0,
  campaignFilterShowTouchstoneChips: false
};

// Selected entity-flight journeys are scoped to whichever audience/location is currently
// in the details panel, so every action that changes or clears that panel must reset them.
/** Every manual fetch layer (AdTech, ADS-B, AIS) and the fetch data timeline range. */
const clearFetchLayers = (state: SliceState) => {
  state.egeLayerRequests = [];
  state.adsbFetchedData = [];
  state.aisFetchedData = [];
  state.fetchDateRange = null;
  // As when the last AdTech fetch layer is removed: there are no fetch results left to sort by
  if (state.sortAudienceBy === 'fetch-results') {
    state.sortAudienceBy = 'score';
  }
};

const clearSelectedEntityFlightJourneys = (state: SliceState) => {
  state.selectedEntityFlightJourneys = [];
  state.selectedEntityVesselJourneys = [];
};

export const slice = createSlice({
  name: 'slice',
  initialState,
  reducers: {
    nextDetailsLocation: (state) => {
      if (state.detailsLocationIdx < state.selectedLocations.length - 1) {
        state.detailsLocationIdx += 1;
        state.detailsLocation = state.selectedLocations[state.detailsLocationIdx];
      } else if (state.selectedLocations.length > 0) {
        state.detailsLocationIdx = 0;
        state.detailsLocation = state.selectedLocations[0];
      }
    },
    prevDetailsLocation: (state) => {
      if (state.detailsLocationIdx > 0) {
        state.detailsLocationIdx -= 1;
        state.detailsLocation = state.selectedLocations[state.detailsLocationIdx];
      } else if (state.selectedLocations.length > 0) {
        state.detailsLocationIdx = state.selectedLocations.length - 1;
        state.detailsLocation = state.selectedLocations[state.detailsLocationIdx];
      }
    },
    nextDetailsAudience: (state) => {
      clearSelectedEntityFlightJourneys(state);
      if (state.detailsAudienceIdx < state.selectedAudience.length - 1) {
        state.detailsAudienceIdx += 1;
        state.detailsAudience = state.selectedAudience[state.detailsAudienceIdx];
      } else if (state.selectedAudience.length > 0) {
        state.detailsAudienceIdx = 0;
        state.detailsAudience = state.selectedAudience[0];
      }
    },
    prevDetailsAudience: (state) => {
      clearSelectedEntityFlightJourneys(state);
      if (state.detailsAudienceIdx > 0) {
        state.detailsAudienceIdx -= 1;
        state.detailsAudience = state.selectedAudience[state.detailsAudienceIdx];
      } else if (state.selectedAudience.length > 0) {
        state.detailsAudienceIdx = state.selectedAudience.length - 1;
        state.detailsAudience = state.selectedAudience[state.detailsAudienceIdx];
      }
    },
    nextDetailsWifi: (state) => {
      if (state.detailsWifiIdx < state.selectedWifi.length - 1) {
        state.detailsWifiIdx += 1;
        state.detailsWifi = state.selectedWifi[state.detailsWifiIdx];
      } else if (state.selectedWifi.length > 0) {
        state.detailsWifiIdx = 0;
        state.detailsWifi = state.selectedWifi[0];
      }
    },
    prevDetailsWifi: (state) => {
      if (state.detailsWifiIdx > 0) {
        state.detailsWifiIdx -= 1;
        state.detailsWifi = state.selectedWifi[state.detailsWifiIdx];
      } else if (state.selectedWifi.length > 0) {
        state.detailsWifiIdx = state.selectedWifi.length - 1;
        state.detailsWifi = state.selectedWifi[state.detailsWifiIdx];
      }
    },
    nextDetailsSenpai: (state) => {
      if (state.selectedSenpaiList.length === 0) return;

      if (state.detailsSenpaiIdx < state.selectedSenpaiList.length - 1) {
        state.detailsSenpaiIdx += 1;
      } else {
        state.detailsSenpaiIdx = 0;
      }

      state.detailsSenpai = state.selectedSenpaiList[state.detailsSenpaiIdx];
    },
    prevDetailsSenpai: (state) => {
      if (state.selectedSenpaiList.length === 0) return;

      if (state.detailsSenpaiIdx > 0) {
        state.detailsSenpaiIdx -= 1;
      } else {
        state.detailsSenpaiIdx = state.selectedSenpaiList.length - 1;
      }

      state.detailsSenpai = state.selectedSenpaiList[state.detailsSenpaiIdx];
    },
    toggleEgeLayer: (state, action: PayloadAction<string>) => {
      const layer = state.egeLayerRequests.find((r) => r.id === action.payload);
      if (layer) {
        layer.enabled = !layer.enabled;
      }
      state.fetchDateRange = null;
      // if no layers are enabled, reset the sort method
      if (state.egeLayerRequests.filter((r) => r.enabled).length === 0 && state.sortAudienceBy === 'fetch-results') {
        state.sortAudienceBy = 'score';
      }
    },
    addEgeLayerRequest: (state, action: PayloadAction<EGELayerRequest>) => {
      state.egeLayerRequests.push(action.payload);
      state.nextFetchLayerNumber += 1;
      state.fetchDateRange = null;
    },
    removeEgeLayerRequest: (state, action: PayloadAction<string>) => {
      state.egeLayerRequests = state.egeLayerRequests.filter((req: EGELayerRequest) => req.id !== action.payload);
      state.fetchDateRange = null;
      // if no layers are enabled, reset the sort method
      if (state.egeLayerRequests.filter((r) => r.enabled).length === 0 && state.sortAudienceBy === 'fetch-results') {
        state.sortAudienceBy = 'score';
      }
    },
    updateEgeLayerRequest: (state, action: PayloadAction<{ id: string; polygons?: GetLocationsResponse[]; entities?: Audience[] }>) => {
      const layer = state.egeLayerRequests.find((r) => r.id === action.payload.id);
      if (layer) {
        if (action.payload.polygons !== undefined) {
          layer.polygons = action.payload.polygons;
        }
        if (action.payload.entities !== undefined) {
          layer.entities = action.payload.entities;
        }
      }
    },
    renameEgeLayerRequest: (state, action: PayloadAction<{ id: string; label: string }>) => {
      const layer = state.egeLayerRequests.find((r) => r.id === action.payload.id);
      if (layer) {
        layer.label = action.payload.label;
      }
    },
    setAdsbFlights: (state, action: PayloadAction<AdsbFlightExt[]>) => {
      state.adsbFlights = action.payload;
    },
    toggleAdsbFetchedData: (state, action: PayloadAction<string>) => {
      const layer = state.adsbFetchedData.find((r) => r.id === action.payload);
      if (layer) {
        layer.enabled = !layer.enabled;
      }
    },
    addAdsbFetchedData: (state, action: PayloadAction<AdsbFetchedData>) => {
      state.adsbFetchedData.push(action.payload);
      state.nextFetchLayerNumber += 1;
    },
    removeAdsbFetchedData: (state, action: PayloadAction<string>) => {
      state.adsbFetchedData = state.adsbFetchedData.filter((req: AdsbFetchedData) => req.id !== action.payload);
    },
    renameAdsbFetchedData: (state, action: PayloadAction<{ id: string; label: string }>) => {
      const layer = state.adsbFetchedData.find((r) => r.id === action.payload.id);
      if (layer) {
        layer.label = action.payload.label;
      }
    },
    toggleAdsbData: (state) => {
      state.isAdsbDataOn = !state.isAdsbDataOn;
    },
    toggleAisFetchedData: (state, action: PayloadAction<string>) => {
      const layer = state.aisFetchedData.find((r) => r.id === action.payload);
      if (layer) {
        layer.enabled = !layer.enabled;
      }
      state.fetchDateRange = null;
    },
    addAisFetchedData: (state, action: PayloadAction<AisFetchedData>) => {
      state.aisFetchedData.push(action.payload);
      state.nextFetchLayerNumber += 1;
      state.fetchDateRange = null;
    },
    removeAisFetchedData: (state, action: PayloadAction<string>) => {
      state.aisFetchedData = state.aisFetchedData.filter((req: AisFetchedData) => req.id !== action.payload);
      state.fetchDateRange = null;
    },
    setFetchDateRange: (state, action: PayloadAction<[number, number] | null>) => {
      state.fetchDateRange = action.payload;
    },
    setShowFetchDataTimeline: (state, action: PayloadAction<boolean>) => {
      state.showFetchDataTimeline = action.payload;
      // Hidden, the timeline can't show its filter, so the map shows all the fetched data
      if (!action.payload) state.fetchDateRange = null;
    },
    renameAisFetchedData: (state, action: PayloadAction<{ id: string; label: string }>) => {
      const layer = state.aisFetchedData.find((r) => r.id === action.payload.id);
      if (layer) {
        layer.label = action.payload.label;
      }
    },
    toggleSenpaiDetections: (state) => {
      state.senpaiDataOn = !state.senpaiDataOn;
    },
    restoreSnapshot: (_, action: PayloadAction<SliceState>) => {
      return { ...initialState, ...action.payload };
    },
    setSenpaiDetails: (state, action: PayloadAction<BurstFeature | null>) => {
      const burst = action.payload;
      const burstId = burst?.properties.burst_id;

      if (!burst || !burstId) {
        state.detailsSenpai = null;
        state.detailsSenpaiIdx = -1;
        return;
      }

      // If the same burst is clicked again, clear the details
      if (state.detailsSenpai?.properties.burst_id === burstId) {
        state.detailsSenpai = null;
        state.detailsSenpaiIdx = -1;
        return;
      }

      state.detailsSenpai = burst;
      state.detailsSenpaiActiveTab = 'burst';
      // update the index of the detail panel to this one
      state.detailsSenpaiIdx = state.selectedSenpaiList.findIndex((b) => b.properties.burst_id === burstId);
    },
    toggleSenpaiSelected: (state, action: PayloadAction<BurstFeature>) => {
      // track what our focus was prior to removing this item
      const detailsTarget = state.detailsSenpaiIdx > -1 ? state.selectedSenpaiList[state.detailsSenpaiIdx] : null;

      const burstId = action.payload.properties.burst_id;

      if (state.selectedSenpaiLookup[burstId]) {
        delete state.selectedSenpaiLookup[burstId];
        state.selectedSenpaiList = state.selectedSenpaiList.filter((b) => b.properties.burst_id !== burstId);
      } else {
        state.selectedSenpaiLookup[burstId] = action.payload;
        state.selectedSenpaiList.push(action.payload);
      }

      if (state.selectedSenpaiList.length === 0) {
        state.senpaiShowOnlySelections = false;
        state.detailsSenpaiIdx = -1;
        state.detailsSenpai = null;
        return;
      }

      if (state.detailsSenpaiIdx === -1) {
        state.detailsSenpaiIdx = state.selectedSenpaiList.length - 1;
        state.detailsSenpai = state.selectedSenpaiList[state.selectedSenpaiList.length - 1];
        return;
      }

      if (detailsTarget) {
        const newIdx = state.selectedSenpaiList.findIndex((b) => b.properties.burst_id === detailsTarget.properties.burst_id);
        if (newIdx !== -1) {
          state.detailsSenpaiIdx = newIdx;
          state.detailsSenpai = state.selectedSenpaiList[newIdx];
        } else {
          // If the focused item was removed, adjust index if needed
          state.detailsSenpaiIdx = Math.min(state.detailsSenpaiIdx, state.selectedSenpaiList.length - 1);
          state.detailsSenpai = state.selectedSenpaiList[state.detailsSenpaiIdx];
        }
      }
    },
    addSenpaiSelected: (state, action: PayloadAction<BurstFeature[]>) => {
      for (const burst of action.payload) {
        const burstId = burst.properties.burst_id;
        if (!state.selectedSenpaiLookup[burstId]) {
          state.selectedSenpaiLookup[burstId] = burst;
          state.selectedSenpaiList.push(burst);
        }
      }
    },
    clearSenpaiDetails: (state) => {
      state.detailsSenpai = null;
      state.detailsSenpaiIdx = -1;
    },
    setSenpaiDetailsActiveTab: (state, action: PayloadAction<'burst' | 'notes'>) => {
      state.detailsSenpaiActiveTab = action.payload;
    },
    setWifiDetails: (state, action: PayloadAction<WIFIRow>) => {
      state.wifiConnectedOrSurveyedData = undefined;
      const compositeKey = getWifiCompositeKey(action.payload.wifi_bssid, action.payload.cluster_id);

      // Get the current composite key if there are current details
      const currentCompositeKey = state.detailsWifi ? getWifiCompositeKey(state.detailsWifi.wifi_bssid, state.detailsWifi.cluster_id) : undefined;

      // If clicking the same WiFi (same composite key) and it's not selected, clear details (toggle off)
      if (currentCompositeKey === compositeKey && !state.selectedWifiLookup[compositeKey]) {
        state.detailsWifi = undefined;
        return;
      } else {
        state.detailsWifi = action.payload;

        const newIdx = state.selectedWifi.findIndex((wifi) => getWifiCompositeKey(wifi.wifi_bssid, wifi.cluster_id) === compositeKey);
        state.detailsWifiIdx = newIdx;
      }
    },
    setHoveredWifi: (state, action: PayloadAction<WIFIRow | undefined>) => {
      state.hoveredWifi = action.payload;
    },
    toggleWifiSelected: (state, action: PayloadAction<WIFIRow>) => {
      // track what our focus was prior to removing this location:
      const detailsTarget = state.detailsWifiIdx > -1 ? state.selectedWifi[state.detailsWifiIdx] : null;

      const compositeKey = getWifiCompositeKey(action.payload.wifi_bssid, action.payload.cluster_id);
      if (state.selectedWifiLookup[compositeKey]) {
        delete state.selectedWifiLookup[compositeKey];
        state.selectedWifi = state.selectedWifi.filter((wifi) => wifi.wifi_bssid !== action.payload.wifi_bssid || wifi.cluster_id !== action.payload.cluster_id);
      } else {
        state.selectedWifiLookup[compositeKey] = action.payload;
        state.selectedWifi.push(action.payload);
      }

      if (state.selectedWifi.length === 0) {
        state.wifiShowOnlySelections = false;
        state.detailsWifiIdx = -1;
        state.detailsWifi = undefined;
        return;
      }

      if (state.detailsWifiIdx === -1) {
        state.detailsWifiIdx = state.selectedWifi.length - 1;
        state.detailsWifi = state.selectedWifi[state.selectedWifi.length - 1];
        return;
      }

      if (detailsTarget) {
        const detailsTargetKey = getWifiCompositeKey(detailsTarget.wifi_bssid, detailsTarget.cluster_id);
        const newIdx = state.selectedWifi.findIndex((wifi) => getWifiCompositeKey(wifi.wifi_bssid, wifi.cluster_id) === detailsTargetKey);
        if (newIdx !== -1) {
          state.detailsWifiIdx = newIdx;
          state.detailsWifi = state.selectedWifi[newIdx];
        } else {
          // If the focused wifi was removed, adjust index if needed
          state.detailsWifiIdx = Math.min(state.detailsWifiIdx, state.selectedWifi.length - 1);
          state.detailsWifi = state.selectedWifi[state.detailsWifiIdx];
        }
      }
    },
    setWifiConnectedOrSurveyedData: (state, action: PayloadAction<GetWifiObservationsSurveyedOrConnectedResponse[] | undefined>) => {
      state.wifiConnectedOrSurveyedData = action.payload;
    },
    setConnectedOrSurveyedLoading: (state, action: PayloadAction<boolean>) => {
      state.wifiConnectedOrSurveyedLoading = action.payload;
    },
    zoomToWifiPoint: (state, action: PayloadAction<{ latitude: number; longitude: number }>) => {
      if (action.payload) {
        const { latitude, longitude } = action.payload;

        let minLat = Number.MAX_SAFE_INTEGER;
        let maxLat = Number.MIN_SAFE_INTEGER;
        let minLng = Number.MAX_SAFE_INTEGER;
        let maxLng = Number.MIN_SAFE_INTEGER;

        minLat = Math.min(minLat, latitude);
        maxLat = Math.max(maxLat, latitude);
        minLng = Math.min(minLng, longitude);
        maxLng = Math.max(maxLng, longitude);

        state.fitBounds = [minLng, minLat, maxLng, maxLat];
        state.fitBoundsImmediately = false;
        state.fitBoundsMaxZoom = 18; // Zoom in closer for wifi points
      }
    },
    clearWifiDetails: (state) => {
      if (state.selectedWifi.length > 0) {
        state.detailsWifiIdx = 0;
        state.detailsWifi = state.selectedWifi[0];
      } else {
        state.detailsWifi = undefined;
        state.detailsWifiIdx = -1;
      }

      state.wifiConnectedOrSurveyedData = undefined;
    },
    addWifis: (state, action: PayloadAction<WIFIRow[]>) => {
      for (const wifi of action.payload) {
        const compositeKey = getWifiCompositeKey(wifi.wifi_bssid, wifi.cluster_id);
        if (!state.selectedWifiLookup[compositeKey]) {
          state.selectedWifi.push(wifi);
          state.selectedWifiLookup[compositeKey] = wifi;
        }
      }
      // Set the first WiFi item as the detail if we have selections
      if (state.selectedWifi.length > 0 && !state.detailsWifi) {
        state.detailsWifiIdx = 0;
        state.detailsWifi = state.selectedWifi[0];
      }
      if (state.selectedWifi.length === 0) state.wifiShowOnlySelections = false;
    },
    replaceWifi: (state, action: PayloadAction<WIFIRow>) => {
      state.selectedWifi = [action.payload];
      const compositeKey = getWifiCompositeKey(action.payload.wifi_bssid, action.payload.cluster_id);
      state.selectedWifiLookup = { [compositeKey]: action.payload };
      state.detailsWifiIdx = 0;
      state.detailsWifi = action.payload;
      if (state.selectedWifi.length === 0) state.wifiShowOnlySelections = false;
    },
    setBannerHasFTPOClassification: (state, action: PayloadAction<boolean>) => {
      state.bannerHasFTPOClassification = action.payload;
    },
    setWifiResults: (state, action: PayloadAction<{ data: WIFIRow[]; totalCount: number; returnedCount: number }>) => {
      // don't replace an emtpy array w/ an empty array (don't circumvent shallowEqual)
      if (state.wifiResults.length === 0 && action.payload.data.length === 0) {
        return;
      }
      state.wifiResults = action.payload.data;
      state.wifiTotalCount = action.payload.totalCount;
      state.wifiReturnedCount = action.payload.returnedCount;
    },
    setWifiResultsLoading: (state, action: PayloadAction<boolean>) => {
      state.wifiResultsLoading = action.payload;
    },
    setLastWifiQueryParams: (state, action: PayloadAction<Record<string, unknown> | undefined>) => {
      state.lastWifiQueryParams = action.payload;
    },
    setDataminrData: (state, action: PayloadAction<DataminrAlertFeatureCollection | undefined>) => {
      state.dataminrData = action.payload;
    },
    setDataminrIsLoading: (state, action: PayloadAction<boolean>) => {
      state.dataminrIsLoading = action.payload;
    },
    setDataminrIsError: (state, action: PayloadAction<boolean>) => {
      state.dataminrIsError = action.payload;
    },
    toggleDataminrData: (state) => {
      state.dataminrDataOn = !state.dataminrDataOn;
    },
    setDataminrDetails: (state, action: PayloadAction<DataminrAlertFeature | null>) => {
      const alert = action.payload;
      const alertId = alert?.properties.alert_id;

      if (!alert || !alertId) {
        state.detailsDataminr = null;
        state.detailsDataminrIdx = -1;
        return;
      }

      // Clicking the open alert again closes the detail panel.
      if (state.detailsDataminr?.properties.alert_id === alertId) {
        state.detailsDataminr = null;
        state.detailsDataminrIdx = -1;
        return;
      }

      state.detailsDataminr = alert;
      state.detailsDataminrActiveTab = 'details';
      state.detailsDataminrIdx = state.selectedDataminrList.findIndex((a) => a.properties.alert_id === alertId);
    },
    clearDataminrDetails: (state) => {
      state.detailsDataminr = null;
      state.detailsDataminrIdx = -1;
    },
    setDataminrDetailsActiveTab: (state, action: PayloadAction<'details' | 'notes'>) => {
      state.detailsDataminrActiveTab = action.payload;
    },
    toggleDataminrDetailCollapsed: (state) => {
      state.dataminrDetailCollapsed = !state.dataminrDetailCollapsed;
    },
    nextDetailsDataminr: (state) => {
      if (state.selectedDataminrList.length === 0) return;

      if (state.detailsDataminrIdx < state.selectedDataminrList.length - 1) {
        state.detailsDataminrIdx += 1;
      } else {
        state.detailsDataminrIdx = 0;
      }

      state.detailsDataminr = state.selectedDataminrList[state.detailsDataminrIdx];
    },
    prevDetailsDataminr: (state) => {
      if (state.selectedDataminrList.length === 0) return;

      if (state.detailsDataminrIdx > 0) {
        state.detailsDataminrIdx -= 1;
      } else {
        state.detailsDataminrIdx = state.selectedDataminrList.length - 1;
      }

      state.detailsDataminr = state.selectedDataminrList[state.detailsDataminrIdx];
    },
    toggleDataminrSelected: (state, action: PayloadAction<DataminrAlertFeature>) => {
      // Remember what the detail panel was focused on before the list changes.
      const detailsTarget = state.detailsDataminrIdx > -1 ? state.selectedDataminrList[state.detailsDataminrIdx] : null;

      const alertId = action.payload.properties.alert_id;

      if (state.selectedDataminrLookup[alertId]) {
        delete state.selectedDataminrLookup[alertId];
        state.selectedDataminrList = state.selectedDataminrList.filter((a) => a.properties.alert_id !== alertId);
      } else {
        state.selectedDataminrLookup[alertId] = action.payload;
        state.selectedDataminrList.push(action.payload);
      }

      if (state.selectedDataminrList.length === 0) {
        state.dataminrShowOnlySelections = false;
        state.detailsDataminrIdx = -1;
        state.detailsDataminr = null;
        return;
      }

      if (state.detailsDataminrIdx === -1) {
        state.detailsDataminrIdx = state.selectedDataminrList.length - 1;
        state.detailsDataminr = state.selectedDataminrList[state.selectedDataminrList.length - 1];
        return;
      }

      if (detailsTarget) {
        const newIdx = state.selectedDataminrList.findIndex((a) => a.properties.alert_id === detailsTarget.properties.alert_id);
        if (newIdx !== -1) {
          state.detailsDataminrIdx = newIdx;
          state.detailsDataminr = state.selectedDataminrList[newIdx];
        } else {
          // The focused alert was removed, so fall back to whatever now sits at that index.
          state.detailsDataminrIdx = Math.min(state.detailsDataminrIdx, state.selectedDataminrList.length - 1);
          state.detailsDataminr = state.selectedDataminrList[state.detailsDataminrIdx];
        }
      }
    },
    addDataminrSelected: (state, action: PayloadAction<DataminrAlertFeature[]>) => {
      for (const alert of action.payload) {
        const alertId = alert.properties.alert_id;
        if (!state.selectedDataminrLookup[alertId]) {
          state.selectedDataminrLookup[alertId] = alert;
          state.selectedDataminrList.push(alert);
        }
      }
    },
    clearSelectedDataminr: (state) => {
      state.selectedDataminrList = [];
      state.selectedDataminrLookup = {};
      state.detailsDataminr = null;
      state.detailsDataminrIdx = -1;
      state.dataminrShowOnlySelections = false;
    },
    toggleShowOnlySelectedDataminr: (state) => {
      state.dataminrShowOnlySelections = !state.dataminrShowOnlySelections;
    },
    setDataminrFilterAlertTypes: (state, action: PayloadAction<DataminrAlertFeature['properties']['alert_type'][]>) => {
      state.dataminrFilterAlertTypes = action.payload;
    },
    setDataminrSort: (state, action: PayloadAction<{ field: DataminrSortField; order: DataminrSortOrder }>) => {
      state.dataminrSortField = action.payload.field;
      state.dataminrSortOrder = action.payload.order;
    },
    setAriData: (state, action: PayloadAction<BurstFeatureCollection | undefined>) => {
      state.ariData = action.payload;
    },
    setAriIsLoading: (state, action: PayloadAction<boolean>) => {
      state.ariIsLoading = action.payload;
    },
    setAriIsError: (state, action: PayloadAction<boolean>) => {
      state.ariIsError = action.payload;
    },
    egeRawToggle: (state) => {
      state.egeRawPlaying = !state.egeRawPlaying;
    },
    shareCampaign: (state, action: PayloadAction<GetCampaignResponse | null>) => {
      state.shareCampaign = action.payload;
    },
    viewCampaignSource: (state, action: PayloadAction<GetCampaignResponse | null>) => {
      state.viewSourceCampaign = action.payload;
      if (action.payload !== null) {
        state.campaignCreatorOpen = true;
      }
    },
    setCampaignH3DwellLayerScaleType: (state, action: PayloadAction<SCALE_TYPE>) => {
      state.campaignH3DwellLayerScaleType = action.payload;
    },
    clearCampaignOnScreenEntities: (state) => {
      state.campaignOnScreenEntities = undefined;
    },
    setCampaignOnScreenEntities: (state, action: PayloadAction<string[]>) => {
      state.campaignOnScreenEntities = action.payload;
    },
    clearSelectedAudience: (state) => {
      state.selectedAudience = [];
      state.selectedAudienceIds = [];
      state.selectedAudienceLookup = {};
      state.detailsAudience = undefined;
      state.detailsAudienceDateRange = undefined;
      clearSelectedEntityFlightJourneys(state);
      state.audienceShowOnlySelections = false;
    },
    clearSelectedLocations: (state) => {
      state.selectedLocations = [];
      state.selectedLocationIds = [];
      state.selectedLocationsLookup = {};
      state.detailsLocation = undefined;
      state.locationShowOnlySelections = false;
    },
    clearSelectedWifi: (state) => {
      state.selectedWifi = [];
      state.selectedWifiLookup = {};
      state.detailsWifi = undefined;
      state.wifiShowOnlySelections = false;
    },
    clearSelectedSenpai: (state) => {
      state.selectedSenpaiList = [];
      state.selectedSenpaiLookup = {};
      state.detailsSenpai = null;
      state.detailsSenpaiIdx = -1;
      state.senpaiShowOnlySelections = false;
    },
    togglePanelHeader: (state) => {
      state.isPanelHeaderVisible = !state.isPanelHeaderVisible;
      // If closing the panel, mark it as manually closed
      if (!state.isPanelHeaderVisible) {
        state.panelHasBeenManuallyClosed = true;
      }
    },
    openPanelForTab: (state) => {
      // Only open if never manually closed before
      if (!state.panelHasBeenManuallyClosed) {
        state.isPanelHeaderVisible = true;
      }
    },
    openPanel: (state) => {
      state.isPanelHeaderVisible = true;
    },
    disableDrawTools: (state) => {
      state.drawToolActive = false;
    },
    enableMagicWand: (state) => {
      state.drawToolActive = true;
      state.measurementActive = false;
      state.tackleboxDrawModeEnabled = false;
    },
    enableMeasurement: (state) => {
      state.measurementActive = true;
      state.drawToolActive = false;
      state.tackleboxDrawModeEnabled = false;
    },
    disableMeasurement: (state) => {
      state.measurementActive = false;
    },
    setDrawTool: (state, action: PayloadAction<DrawTool>) => {
      state.drawToolSelected = action.payload;
    },
    setDrawnBboxBounds: (state, action: PayloadAction<string | undefined>) => {
      state.drawnBboxBounds = action.payload;
    },
    addDrawnEgePolygon: (state, action: PayloadAction<string>) => {
      state.drawnEgePolygons.push({ wkt: action.payload, visible: true });
      state.drawnBboxBounds = combineWktGeometries(state.drawnEgePolygons.map((p) => p.wkt)) ?? undefined;
    },
    removeDrawnEgePolygon: (state, action: PayloadAction<number>) => {
      state.drawnEgePolygons = state.drawnEgePolygons.filter((_, i) => i !== action.payload);
      state.drawnBboxBounds = combineWktGeometries(state.drawnEgePolygons.map((p) => p.wkt)) ?? undefined;
    },
    toggleDrawnEgePolygon: (state, action: PayloadAction<number>) => {
      // Toggles map preview visibility only; submitted bounds always include all drawn polygons.
      const polygon = state.drawnEgePolygons[action.payload];
      if (!polygon) return;
      polygon.visible = !polygon.visible;
    },
    clearDrawnEgePolygons: (state) => {
      state.drawnEgePolygons = [];
      state.drawnBboxBounds = undefined;
    },
    setTackleboxAdminLayer: (state, action: PayloadAction<string | null>) => {
      state.tackleboxAdminLayer = action.payload;
    },
    setTackleboxDrawnPolygonSelected: (state, action: PayloadAction<string>) => {
      // Toggle: if already selected, deselect it
      if (state.tackleboxDrawnPolygonSelected === action.payload) {
        state.tackleboxDrawnPolygonSelected = '';
      } else {
        state.tackleboxDrawnPolygonSelected = action.payload;
      }
    },
    setTackleboxDrawMode: (state, action: PayloadAction<boolean>) => {
      state.tackleboxDrawModeEnabled = action.payload;
      if (action.payload) {
        state.drawToolActive = false;
        state.measurementActive = false;
      } else {
        state.tackleboxDrawingSlotId = null;
      }
    },
    addTackleboxPolygon: (state, action: PayloadAction<Feature>) => {
      if (state.tackleboxDrawingSlotId !== null) {
        // Fill the active drawing slot
        const idx = state.tackleboxSlots.findIndex((s) => s.id === state.tackleboxDrawingSlotId);
        if (idx >= 0) {
          state.tackleboxSlots[idx] = { ...state.tackleboxSlots[idx], polygon: action.payload };
        }
        state.tackleboxDrawingSlotId = null;
      } else {
        // No active drawing slot — fill the first empty slot, or append a new one
        const emptyIdx = state.tackleboxSlots.findIndex((s) => !s.polygon);
        if (emptyIdx >= 0) {
          state.tackleboxSlots[emptyIdx] = { ...state.tackleboxSlots[emptyIdx], polygon: action.payload };
        } else {
          const newId = Math.max(...state.tackleboxSlots.map((s) => s.id)) + 1;
          state.tackleboxSlots.push({ id: newId, polygon: action.payload });
        }
      }
    },
    completeDrawnPolygon: (state, action: PayloadAction<Feature>) => {
      if (state.tackleboxDrawingSlotId !== null) {
        const idx = state.tackleboxSlots.findIndex((s) => s.id === state.tackleboxDrawingSlotId);
        if (idx >= 0) {
          let defaultName = 'Polygon 1';
          if (!action.payload.properties?.name) {
            const existingNames = state.tackleboxSlots.flatMap((s) => (s.polygon?.properties?.name ? [s.polygon.properties.name] : []));
            let highest = 0;
            for (const n of existingNames) {
              const match = n.match(/^Polygon (\d+)$/);
              if (match) highest = Math.max(highest, parseInt(match[1], 10));
            }
            defaultName = `Polygon ${highest + 1}`;
          }

          const name = action.payload.properties?.name || defaultName;
          const polygon = { ...action.payload, properties: { ...action.payload.properties, name } };
          state.tackleboxSlots[idx] = { ...state.tackleboxSlots[idx], polygon };
        }
        state.tackleboxDrawingSlotId = null;
      }
      state.tackleboxDrawModeEnabled = false;
    },
    setSlotPolygon: (state, action: PayloadAction<{ slotId: number; polygon: Feature | null }>) => {
      const idx = state.tackleboxSlots.findIndex((s) => s.id === action.payload.slotId);
      if (idx >= 0) {
        state.tackleboxSlots[idx] = action.payload.polygon ? { ...state.tackleboxSlots[idx], polygon: action.payload.polygon } : { id: state.tackleboxSlots[idx].id };
      }
    },
    setTackleboxHistoryTab: (state, action: PayloadAction<'mine' | 'shared'>) => {
      state.tackleboxHistoryTab = action.payload;
    },
    resetTackleboxParams: (state) => {
      state.tackleboxLoadedJobId = undefined;
      state.tackleboxJobName = '';
      state.tackleboxMinTitoPolygons = 2;
      state.tackleboxSlots = [{ id: 0 }, { id: 1 }];
      state.tackleboxDrawingSlotId = null;
      state.tackleboxDrawModeEnabled = false;
    },
    setTackleboxSlots: (state, action: PayloadAction<{ id: number; polygon?: Feature }[]>) => {
      state.tackleboxSlots = action.payload;
    },
    setTackleboxDrawingSlotId: (state, action: PayloadAction<number | null>) => {
      state.tackleboxDrawingSlotId = action.payload;
    },
    setTackleboxJobName: (state, action: PayloadAction<string>) => {
      state.tackleboxJobName = action.payload;
    },
    setTackleboxMinTitoPolygons: (state, action: PayloadAction<number>) => {
      state.tackleboxMinTitoPolygons = action.payload;
    },
    setTackleboxParams: (state, action: PayloadAction<{ tacklebox_job_id: string; features: Feature[]; name: string; min_polygons: number }>) => {
      const { features, tacklebox_job_id, name, min_polygons } = action.payload;
      state.tackleboxLoadedJobId = tacklebox_job_id;
      state.tackleboxJobName = name;
      state.tackleboxMinTitoPolygons = min_polygons;
      state.tackleboxDrawingSlotId = null;
      const newSlots = features.map((p, i) => ({ id: i, polygon: p as Feature }));
      state.tackleboxSlots = newSlots.length >= 2 ? newSlots : [...newSlots, ...Array.from({ length: 2 - newSlots.length }, (_, i) => ({ id: newSlots.length + i }))];
    },
    setSelectedLocationAttrPanel: (state, action: PayloadAction<string | undefined>) => {
      state.selectedLocationAttrPanel = action.payload;
    },
    setEgeRawDateRange: (state, action: PayloadAction<undefined | [number, number]>) => {
      state.egeRawDateRange = action.payload;
    },
    setCampaignClusterDateRange: (state, action: PayloadAction<undefined | [number, number]>) => {
      state.campaignClusterDateRange = action.payload;
    },
    setCampaignH3DwellLayerDate: (state, action: PayloadAction<undefined | [number, number]>) => {
      state.campaignH3DwellLayerDate = action.payload;
    },
    setCampaignRawAggSize: (state, action: PayloadAction<number>) => {
      state.campaignRawAggSize = action.payload;
    },
    setEventCountColorOverride: (state, action: PayloadAction<string | undefined>) => {
      state.eventCountColorOverride = action.payload;
    },
    setCampaignH3DwellLayerRes: (state, action: PayloadAction<number>) => {
      state.campaignH3DwellLayerRes = action.payload;
    },
    setCampaignH3DwellLayerDim: (state, action: PayloadAction<CampaignDwellAggDimKeys>) => {
      state.campaignH3DwellLayerDim = action.payload;
    },
    toggleCampaignH3DwellLayerScaleFilter: (state, action: PayloadAction<number>) => {
      const idx = state.campaignH3DwellLayerScaleFilter.indexOf(action.payload);
      if (idx === -1) {
        state.campaignH3DwellLayerScaleFilter.push(action.payload);
      } else {
        state.campaignH3DwellLayerScaleFilter.splice(idx, 1);
      }
    },
    setCampaignRawAggDimension: (state, action: PayloadAction<CampaignRawAggDimensions>) => {
      state.campaignRawAggDimension = action.payload;
    },
    setCampaign: (state, action: PayloadAction<{ campaign: undefined | null | GetCampaignResponse; fitBounds: boolean }>) => {
      const { campaign, fitBounds } = action.payload;
      state.campaign = campaign;
      if (campaign) {
        state.egeRawDateRange = [dayjs.utc(campaign.min_record_date).valueOf(), dayjs.utc(campaign.max_record_date).valueOf()];
        state.drawToolSelected = 'wand';
        state.polygonMode = 'global';
        state.isEgeDataOn = true;
        // Fetch layers belong outside a campaign, and none can be fetched during one
        clearFetchLayers(state);
      } else {
        state.egeRawDateRange = undefined;
        state.drawToolSelected = 'fetch-ege';

        state.polygonMode = state.polygonMode === 'campaign' || state.polygonMode === 'global' ? 'global' : 'off';
      }
      if (fitBounds && campaign?.bounds) {
        state.fitBounds = campaign.bounds;
      }
      // disable tools
      state.drawToolActive = false;

      // clear all selections
      state.selectedAudience = [];
      state.selectedAudienceIds = [];
      state.selectedAudienceLookup = {};
      state.selectedLocationIds = [];
      state.selectedLocations = [];
      state.selectedLocationsLookup = {};
      state.detailsAudience = undefined;
      state.detailsLocation = undefined;
      state.selectedAudienceColorOverride = {};
      state.campaignEntityColors = {};
      state.locationShowOnlySelections = false;
      state.audienceShowOnlySelections = false;
      state.detailsAudienceDateRange = undefined;

      state.audienceFilterMinPolygons = 0;
      state.audienceFilterMinScore = 0;
      state.audienceFilterEntityTypes = [...AUDIENCE_ENTITY_TYPE_OPTIONS];
      state.audienceFilterFlightEvidenceTypes = [];
      state.audienceFilterFlightMilitaryTypes = [];
      state.audienceFilterVesselEvidenceTypes = [];
      clearSelectedEntityFlightJourneys(state);
      // state.locationFilterCategory = [];
      state.locationFilterCountry = [];
      state.locationFilterMinUniqueEntities = 0;
      state.locationFilterAdvertiserIds = [];
    },
    // Update the open campaign in place (e.g. a warm published another month) without the
    // reset setCampaign does: selections, filters and the map position are kept.
    refreshCampaign: (state, action: PayloadAction<GetCampaignResponse>) => {
      const campaign = action.payload;
      if (!state.campaign || state.campaign.id !== campaign.id) {
        return;
      }
      if (state.campaign.updated_at === campaign.updated_at && state.campaign.status === campaign.status) {
        return;
      }
      const previousSpan = [dayjs.utc(state.campaign.min_record_date).valueOf(), dayjs.utc(state.campaign.max_record_date).valueOf()];
      // a slider left on the whole span follows it as it grows; a narrowed one is kept as is
      if (state.egeRawDateRange?.[0] === previousSpan[0] && state.egeRawDateRange?.[1] === previousSpan[1]) {
        state.egeRawDateRange = [dayjs.utc(campaign.min_record_date).valueOf(), dayjs.utc(campaign.max_record_date).valueOf()];
      }
      state.campaign = campaign;
    },
    setCampaignSearchText: (state, action: PayloadAction<string>) => {
      state.campaignSearchText = action.payload;
    },
    setCampaignFilters: (
      state,
      action: PayloadAction<{
        campaignEndDate?: number | null;
        touchstoneStartDate: number | null;
        touchstoneEndDate: number | null;
        edgeStartDate: number | null;
        edgeEndDate: number | null;
        continuous: boolean;
        minScore: number;
        showTouchstoneChips: boolean;
      }>
    ) => {
      state.campaignFilterEndDate = action.payload.campaignEndDate;
      state.campaignFilterTouchstoneStartDate = action.payload.touchstoneStartDate;
      state.campaignFilterTouchstoneEndDate = action.payload.touchstoneEndDate;
      state.campaignFilterEdgeStartDate = action.payload.edgeStartDate;
      state.campaignFilterEdgeEndDate = action.payload.edgeEndDate;
      state.campaignFilterContinuousCampaign = action.payload.continuous;
      state.campaignFilterMinScore = action.payload.minScore;
      state.campaignFilterShowTouchstoneChips = action.payload.showTouchstoneChips;
    },
    resetCampaignFilters: (state) => {
      // Reset to initial values
      state.campaignFilterEndDate = undefined;
      state.campaignFilterTouchstoneStartDate = dayjs().subtract(90, 'day').startOf('day').valueOf();
      state.campaignFilterTouchstoneEndDate = dayjs().endOf('day').valueOf();
      state.campaignFilterEdgeStartDate = dayjs().subtract(90, 'day').startOf('day').valueOf();
      state.campaignFilterEdgeEndDate = dayjs().endOf('day').valueOf();
      state.campaignFilterContinuousCampaign = false;
      state.campaignFilterMinScore = 0;
      state.campaignFilterShowTouchstoneChips = false;
    },
    toggleCampaignCreator: (state) => {
      state.campaignCreatorOpen = !state.campaignCreatorOpen;
    },
    togglePanelCampaignCreator: (state) => {
      state.isPanelCampaignCreatorOpen = !state.isPanelCampaignCreatorOpen;
      if (state.isPanelCampaignCreatorOpen) {
        state.isPanelFetchOpen = false;
      }
    },
    closePanelCampaignCreator: (state) => {
      state.isPanelCampaignCreatorOpen = false;
    },
    openPanelFetch: (state, action: PayloadAction<string | undefined>) => {
      state.isPanelFetchOpen = true;
      state.isPanelCampaignCreatorOpen = false;
      state.isPanelFetchCollapsed = false;
      state.panelFetchDataset = action.payload ?? '';
    },
    closePanelFetch: (state) => {
      state.isPanelFetchOpen = false;
      state.isPanelFetchCollapsed = false;
      state.panelFetchDataset = '';
    },
    setPanelFetchDataset: (state, action: PayloadAction<string>) => {
      state.panelFetchDataset = action.payload;
    },
    togglePanelFetchCollapsed: (state) => {
      state.isPanelFetchCollapsed = !state.isPanelFetchCollapsed;
    },
    renderEGE: (state) => {
      state.isEgeDataOn = true;
    },
    toggleShowOnlySelectedLocations: (state) => {
      state.locationShowOnlySelections = !state.locationShowOnlySelections;
    },
    toggleShowOnlySelectedWifi: (state) => {
      state.wifiShowOnlySelections = !state.wifiShowOnlySelections;
    },
    toggleShowOnlySelectedAudience: (state) => {
      state.audienceShowOnlySelections = !state.audienceShowOnlySelections;
    },
    toggleShowOnlySelectedSenpai: (state) => {
      state.senpaiShowOnlySelections = !state.senpaiShowOnlySelections;
    },
    setAudienceMinPolygons: (state, action: PayloadAction<number>) => {
      state.audienceFilterMinPolygons = action.payload;
    },
    setAudienceMinScore: (state, action: PayloadAction<number>) => {
      state.audienceFilterMinScore = action.payload;
    },
    setAudienceFilterEntityTypes: (state, action: PayloadAction<AudienceEntityTypeFilter[]>) => {
      state.audienceFilterEntityTypes = action.payload;
      state.isAdsbDataOn = action.payload.includes(AUDIENCE_ENTITY_TYPE.adsb);
    },
    commitAudienceFilters: (
      state,
      action: PayloadAction<{
        minPolygons: number;
        minScore: number;
        entityTypes: AudienceEntityTypeFilter[];
        flightEvidenceTypes: EntityFlightEvidenceType[];
        flightMilitaryTypes: EntityFlightMilitaryType[];
        vesselEvidenceTypes: EntityVesselEvidenceType[];
      }>
    ) => {
      state.audienceFilterMinPolygons = action.payload.minPolygons;
      state.audienceFilterMinScore = action.payload.minScore;
      state.audienceFilterEntityTypes = action.payload.entityTypes;
      state.audienceFilterFlightEvidenceTypes = action.payload.flightEvidenceTypes;
      state.audienceFilterFlightMilitaryTypes = action.payload.flightMilitaryTypes;
      state.audienceFilterVesselEvidenceTypes = action.payload.vesselEvidenceTypes;
      state.isAdsbDataOn = action.payload.entityTypes.includes(AUDIENCE_ENTITY_TYPE.adsb);
    },
    clearLocationFilterCountry: (state) => {
      state.locationFilterCountry = [];
    },
    clearLocationFilterCategory: (state) => {
      state.locationFilterCategory = [];
    },
    addLocationFilterCountry: (state, action: PayloadAction<GadmL0ResponseRow[]>) => {
      const existingISO3 = state.locationFilterCountry.map((c) => c.iso3);
      const newCountries = action.payload.filter((c) => !existingISO3.includes(c.iso3));
      state.locationFilterCountry = [...state.locationFilterCountry, ...newCountries];
    },
    addLocationFilterCategory: (state, action: PayloadAction<string[]>) => {
      state.locationFilterCategory = [...new Set([...state.locationFilterCategory, ...action.payload])];
    },
    toggleLocationFilterCountry: (state, action: PayloadAction<GadmL0ResponseRow>) => {
      const idx = state.locationFilterCountry.findIndex((c) => c.iso3 === action.payload.iso3);
      if (idx === -1) {
        state.locationFilterCountry.push(action.payload);
      } else {
        state.locationFilterCountry.splice(idx, 1);
      }
    },
    toggleLocationFilterCategory: (state, action: PayloadAction<string>) => {
      const idx = state.locationFilterCategory.indexOf(action.payload);
      if (idx === -1) {
        state.locationFilterCategory.push(action.payload);
      } else {
        state.locationFilterCategory.splice(idx, 1);
      }
    },
    commitLocationFilters: (
      state,
      action: PayloadAction<{
        category: string[];
        country: GadmL0ResponseRow[];
        minUniqueEntities: number;
        advertiserIds: string[];
      }>
    ) => {
      state.locationFilterCategory = action.payload.category;
      state.locationFilterCountry = action.payload.country;
      state.locationFilterMinUniqueEntities = action.payload.minUniqueEntities;
      state.locationFilterAdvertiserIds = action.payload.advertiserIds;
    },
    setLocationFilterMinUniqueEntities: (state, action: PayloadAction<number>) => {
      state.locationFilterMinUniqueEntities = action.payload;
    },
    setLocationFilterAdvertiserIds: (state, action: PayloadAction<string[]>) => {
      state.locationFilterAdvertiserIds = action.payload;
    },
    setLocationSort: (state, action: PayloadAction<GetLocationSort>) => {
      state.sortLocationsBy = action.payload;
    },
    toggleLocationSortOrder: (state) => {
      state.locationSortOrder = state.locationSortOrder === 'asc' ? 'desc' : 'asc';
    },
    setAudienceSort: (state, action: PayloadAction<GetAudienceSort>) => {
      state.sortAudienceBy = action.payload;
    },
    toggleAudienceSortOrder: (state) => {
      state.audienceSortOrder = state.audienceSortOrder === 'asc' ? 'desc' : 'asc';
    },
    setWifiSort: (state, action: PayloadAction<GetWifiSort>) => {
      state.sortWifiBy = action.payload;
    },
    toggleWifiSortOrder: (state) => {
      state.wifiSortOrder = state.wifiSortOrder === 'asc' ? 'desc' : 'asc';
    },
    setSenpaiFilterMinRfiCount: (state, action: PayloadAction<number>) => {
      state.senpaiFilterMinRfiCount = action.payload;
    },
    setSenpaiSort: (state, action: PayloadAction<{ field: SenpaiSortField; order: SenpaiSortOrder }>) => {
      state.senpaiSortField = action.payload.field;
      state.senpaiSortOrder = action.payload.order;
    },
    setAudienceDetailsDateRange: (state, action: PayloadAction<[number, number]>) => {
      state.detailsAudienceDateRange = action.payload;
    },
    clearAudienceDetailsDateRange: (state) => {
      state.detailsAudienceDateRange = undefined;
    },
    setAudienceDetails: (state, action: PayloadAction<Audience>) => {
      clearSelectedEntityFlightJourneys(state);
      if (state.detailsAudience?.advertiser_id === action.payload.advertiser_id) {
        // if the same audience is clicked again, clear the details:
        state.detailsAudience = undefined;
        state.detailsLocation = undefined;
        return;
      }
      state.detailsAudience = action.payload;
      state.detailsLocation = undefined;
      state.isPanelFetchOpen = false;
      // update the index of the detail panel to this one..
      state.detailsAudienceIdx = state.selectedAudienceIds.indexOf(action.payload.advertiser_id);
    },
    clearAudienceDetails: (state) => {
      state.detailsAudience = undefined;
      state.detailsLocation = undefined;
      state.detailsAudienceActiveTab = 'attributes';
      clearSelectedEntityFlightJourneys(state);
      // if there are more audiences selected, set the detailsAudience to the first one:
      if (state.selectedAudience.length > 0) {
        state.detailsAudienceIdx = 0;
      } else {
        state.detailsAudienceIdx = -1;
      }
    },
    setAudienceDetailsActiveTab: (state, action: PayloadAction<AudienceDetailsTab>) => {
      state.detailsAudienceActiveTab = action.payload;
    },
    setSelectedEntityFlightJourneys: (state, action: PayloadAction<EntityFlightJourney[]>) => {
      state.selectedEntityFlightJourneys = action.payload;
    },
    toggleSelectedEntityFlightJourney: (state, action: PayloadAction<EntityFlightJourney>) => {
      const journey = action.payload;
      const journeyId = getEntityFlightJourneyId(journey);
      const selectedIndex = state.selectedEntityFlightJourneys.findIndex((selected) => getEntityFlightJourneyId(selected) === journeyId);
      if (selectedIndex >= 0) state.selectedEntityFlightJourneys.splice(selectedIndex, 1);
      else state.selectedEntityFlightJourneys.push(journey);
    },
    setSelectedEntityVesselJourneys: (state, action: PayloadAction<EntityVesselJourney[]>) => {
      state.selectedEntityVesselJourneys = action.payload;
    },
    toggleSelectedEntityVesselJourney: (state, action: PayloadAction<EntityVesselJourney>) => {
      const journey = action.payload;
      const journeyId = getEntityVesselJourneyId(journey);
      const selectedIndex = state.selectedEntityVesselJourneys.findIndex((selected) => getEntityVesselJourneyId(selected) === journeyId);
      if (selectedIndex >= 0) state.selectedEntityVesselJourneys.splice(selectedIndex, 1);
      else state.selectedEntityVesselJourneys.push(journey);
    },
    addSelectedEntityVesselJourney: (state, action: PayloadAction<EntityVesselJourney>) => {
      const journey = action.payload;
      const journeyId = getEntityVesselJourneyId(journey);
      const isSelected = state.selectedEntityVesselJourneys.some((selected) => getEntityVesselJourneyId(selected) === journeyId);
      if (!isSelected) state.selectedEntityVesselJourneys.push(journey);
    },
    setEntityFlightJourneyColor: (state, action: PayloadAction<{ journeyId: string; color: string }>) => {
      state.entityFlightJourneyColors[action.payload.journeyId] = action.payload.color;
    },
    addSelectedEntityFlightJourney: (state, action: PayloadAction<EntityFlightJourney>) => {
      const journey = action.payload;
      const journeyId = getEntityFlightJourneyId(journey);
      const isSelected = state.selectedEntityFlightJourneys.some((selected) => getEntityFlightJourneyId(selected) === journeyId);
      if (!isSelected) state.selectedEntityFlightJourneys.push(journey);
    },
    setAdsbDetailsActiveTab: (state, action: PayloadAction<AdsbDetailsTab>) => {
      state.detailsAdsbActiveTab = action.payload;
    },
    toggleAdsbDetailCollapsed: (state) => {
      state.adsbDetailCollapsed = !state.adsbDetailCollapsed;
    },
    setAudienceColor: (state, action: PayloadAction<{ advertiser_id: string; color: string }>) => {
      state.selectedAudienceColorOverride[action.payload.advertiser_id] = action.payload.color;
    },
    clearAudienceColors: (state) => {
      state.selectedAudienceColorOverride = {};
    },
    setAudienceColors: (state, action: PayloadAction<Record<string, string>>) => {
      // merge the new colors with the existing colors:
      state.selectedAudienceColorOverride = { ...action.payload, ...state.selectedAudienceColorOverride };
    },
    setCampaignEntityColors: (state, action: PayloadAction<Record<string, string>>) => {
      // Merge new campaign entity colors with existing ones (don't overwrite)
      state.campaignEntityColors = { ...state.campaignEntityColors, ...action.payload };
    },
    clearCampaignEntityColors: (state) => {
      state.campaignEntityColors = {};
    },
    setLocationDetails: (state, action: PayloadAction<GetLocationsResponse>) => {
      if (state.detailsLocation?.polygon_id === action.payload.polygon_id) {
        // if the same location is clicked again, clear the details:
        state.detailsLocation = undefined;
        state.detailsAudience = undefined;
        clearSelectedEntityFlightJourneys(state);
        return;
      }
      state.detailsLocation = action.payload;
      state.detailsAudience = undefined;
      clearSelectedEntityFlightJourneys(state);
      state.isPanelFetchOpen = false;
      state.detailsLocationActiveTab = 'touchstone';
      // update the index of the detail panel to this one..
      state.detailsLocationIdx = state.selectedLocationIds.indexOf(action.payload.polygon_id);
    },
    setLocationDetailsActiveTab: (state, action: PayloadAction<'touchstone' | 'profile' | 'units' | 'notes'>) => {
      state.detailsLocationActiveTab = action.payload;
    },
    clearLocationDetails: (state) => {
      state.detailsLocation = undefined;
      state.detailsAudience = undefined;
      clearSelectedEntityFlightJourneys(state);
      // if there are more locations selected, set the detailsLocation to the first one:
      if (state.selectedLocations.length > 0) {
        state.detailsLocationIdx = 0;
      } else {
        state.detailsLocationIdx = -1;
      }
    },
    replaceAudience: (state, action: PayloadAction<Audience>) => {
      state.selectedAudience = [action.payload];
      state.selectedAudienceIds = [action.payload.advertiser_id];
      state.selectedAudienceLookup = { [action.payload.advertiser_id]: action.payload };
      if (!state.selectedAudienceColorOverride[action.payload.advertiser_id]) {
        state.selectedAudienceColorOverride[action.payload.advertiser_id] = getColorFromAdvertiserId(action.payload.advertiser_id);
      }
      // when a new audience is toggled "on", also enable its detail panel:
      state.detailsLocation = undefined;
      state.detailsAudience = action.payload;
      clearSelectedEntityFlightJourneys(state);
      state.isPanelFetchOpen = false;
      if (state.selectedAudienceIds.length === 0) state.audienceShowOnlySelections = false;
    },
    toggleAudience: (state, action: PayloadAction<Audience>) => {
      if (state.selectedAudienceIds.includes(action.payload.advertiser_id)) {
        state.selectedAudienceIds = state.selectedAudienceIds.filter((id) => id !== action.payload.advertiser_id);
        state.selectedAudience = state.selectedAudience.filter((a) => a.advertiser_id !== action.payload.advertiser_id);
        delete state.selectedAudienceLookup[action.payload.advertiser_id];
      } else {
        state.selectedAudience.push(action.payload);
        state.selectedAudienceIds.push(action.payload.advertiser_id);
        state.selectedAudienceLookup[action.payload.advertiser_id] = action.payload;
        if (!state.selectedAudienceColorOverride[action.payload.advertiser_id]) {
          state.selectedAudienceColorOverride[action.payload.advertiser_id] = getColorFromAdvertiserId(action.payload.advertiser_id);
        }
      }

      if (state.selectedAudienceIds.length === 0) {
        state.audienceShowOnlySelections = false;
        state.detailsAudienceIdx = -1;
        return;
      }

      if (state.selectedAudienceIds.length > 0) {
        if (state.detailsAudience) {
          const idx = state.selectedAudienceIds.indexOf(state.detailsAudience.advertiser_id);
          state.detailsAudienceIdx = idx !== -1 ? idx : -1;
        }
      }

      if (state.campaign) {
        state.egeRawDateRange = [dayjs.utc(state.campaign.min_record_date).valueOf(), dayjs.utc(state.campaign.max_record_date).valueOf()];
        state.campaignTimelineRange = 'All';
      }
    },
    setSelectedDatePreset: (state, action: PayloadAction<string>) => {
      state.campaignTimelineRange = action.payload;
    },
    addAudience: (state, action: PayloadAction<Audience[]>) => {
      // Track newly selected entities for color assignment
      const newlySelectedEntities: string[] = [];

      for (const loc of action.payload) {
        if (!state.selectedAudienceLookup[loc.advertiser_id]) {
          state.selectedAudience.push(loc);
          state.selectedAudienceLookup[loc.advertiser_id] = loc;
          state.selectedAudienceIds.push(loc.advertiser_id);
          newlySelectedEntities.push(loc.advertiser_id);
        }
      }

      // Automatically assign colors to newly selected entities
      for (const advertiserId of newlySelectedEntities) {
        if (!state.selectedAudienceColorOverride[advertiserId]) {
          state.selectedAudienceColorOverride[advertiserId] = getColorFromAdvertiserId(advertiserId);
        }
      }

      if (state.selectedAudienceIds.length === 0) state.audienceShowOnlySelections = false;
    },
    addLocations: (state, action: PayloadAction<GetLocationsResponse[]>) => {
      for (const loc of action.payload) {
        if (!state.selectedLocationsLookup[loc.polygon_id]) {
          state.selectedLocations.push(loc);
          state.selectedLocationsLookup[loc.polygon_id] = loc;
          state.selectedLocationIds.push(loc.polygon_id);
        }
      }
      if (state.selectedLocationIds.length === 0) state.locationShowOnlySelections = false;
    },
    onMagicWandFinish: (state, action: PayloadAction<{ locations: GetLocationsResponse[]; audience: Audience[] }>) => {
      for (const loc of action.payload.locations) {
        if (!state.selectedLocationsLookup[loc.polygon_id]) {
          state.selectedLocations.push(loc);
          state.selectedLocationsLookup[loc.polygon_id] = loc;
          state.selectedLocationIds.push(loc.polygon_id);
        }
      }
      for (const loc of action.payload.audience) {
        if (!state.selectedAudienceLookup[loc.advertiser_id]) {
          state.selectedAudience.push(loc);
          state.selectedAudienceLookup[loc.advertiser_id] = loc;
          state.selectedAudienceIds.push(loc.advertiser_id);
        }

        // show only seelected
        state.locationShowOnlySelections = true;
      }
      // state.audienceShowOnlySelections = true;
      // disable tools
      state.drawToolActive = false;
    },
    replaceLocation: (state, action: PayloadAction<GetLocationsResponse>) => {
      state.selectedLocations = [action.payload];
      state.selectedLocationIds = [action.payload.polygon_id];
      state.selectedLocationsLookup = { [action.payload.polygon_id]: action.payload };
      // when a new location is toggled "on", also enable its detail panel:
      // state.detailsAudience = undefined;
      // state.detailsLocation = action.payload;
      if (state.selectedLocationIds.length === 0) state.locationShowOnlySelections = false;

      // do not allow sorting by entities if more than one location is selected
      if (state.selectedLocationIds.length > 1 && state.sortAudienceBy === 'score') {
        state.sortAudienceBy = 'polygons';
      }
    },
    // Silent toggleLocation does not enable the detail panel
    toggleLocationSilent: (state, action: PayloadAction<GetLocationsResponse>) => {
      if (state.selectedLocationIds.includes(action.payload.polygon_id)) {
        state.selectedLocationIds = state.selectedLocationIds.filter((id) => id !== action.payload.polygon_id);
        state.selectedLocations = state.selectedLocations.filter((l) => l.polygon_id !== action.payload.polygon_id);
        delete state.selectedLocationsLookup[action.payload.polygon_id];
      } else {
        state.selectedLocations.push(action.payload);
        state.selectedLocationIds.push(action.payload.polygon_id);
        state.selectedLocationsLookup[action.payload.polygon_id] = action.payload;
      }

      // do not allow sorting by entities if more than one location is selected
      if (state.selectedLocationIds.length > 1 && state.sortAudienceBy === 'score') {
        state.sortAudienceBy = 'polygons';
      }

      if (state.selectedLocationIds.length === 0) {
        state.selectedAudience = [];
        state.selectedAudienceIds = [];
        state.selectedAudienceLookup = {};
        state.detailsAudience = undefined;
        state.detailsAudienceDateRange = undefined;
        clearSelectedEntityFlightJourneys(state);
        state.audienceShowOnlySelections = false;
        state.locationShowOnlySelections = false;
      }
    },
    toggleLocation: (state, action: PayloadAction<GetLocationsResponse>) => {
      if (state.selectedLocationIds.includes(action.payload.polygon_id)) {
        state.selectedLocationIds = state.selectedLocationIds.filter((id) => id !== action.payload.polygon_id);
        state.selectedLocations = state.selectedLocations.filter((l) => l.polygon_id !== action.payload.polygon_id);
        delete state.selectedLocationsLookup[action.payload.polygon_id];
      } else {
        state.selectedLocations.push(action.payload);
        state.selectedLocationIds.push(action.payload.polygon_id);
        state.selectedLocationsLookup[action.payload.polygon_id] = action.payload;
      }

      // do not allow sorting by entities if more than one location is selected
      if (state.selectedLocationIds.length > 1 && state.sortAudienceBy === 'score') {
        state.sortAudienceBy = 'polygons';
      } else if (state.selectedLocationIds.length === 1) {
        state.sortAudienceBy = 'score';
      }

      if (state.selectedLocationIds.length === 0) {
        state.locationShowOnlySelections = false;
        state.detailsLocationIdx = -1;
        return;
      }

      if (state.selectedLocationIds.length > 0) {
        if (state.detailsLocation) {
          const idx = state.selectedLocationIds.indexOf(state.detailsLocation.polygon_id);
          state.detailsLocationIdx = idx !== -1 ? idx : -1;
        }
      }
    },
    toggleLocationNetwork: (state) => {
      state.isLocationNetworkEnabled = !state.isLocationNetworkEnabled;
    },
    setLocationNetworkPolygonId: (state, action: PayloadAction<string | undefined>) => {
      state.locationNetworkPolygonId = action.payload;
      state.isLocationNetworkEnabled = true;
    },
    clearAllSelections: (state) => {
      state.isEgeDataOn = true;
      state.polygonMode = 'global';
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    setActivePage: (state, action: PayloadAction<string>) => {
      state.activePage = action.payload;
    },
    setDelayedMapViewport: (state, action: PayloadAction<{ viewport: MapViewport; bounds: [[number, number], [number, number]] }>) => {
      state.delayedMapViewport = action.payload.viewport;
      state.delayedMapBounds = action.payload.bounds;

      const [southWest, northEast] = action.payload.bounds;
      const delayedMapBoundsBox = {
        northEast: {
          lat: northEast[1],
          lng: northEast[0]
        },
        southWest: {
          lat: southWest[1],
          lng: southWest[0]
        }
      };
      state.delayedMapBoundsBox = delayedMapBoundsBox;
      state.delayedMapBoundsWKT = mapBoundsWKT(delayedMapBoundsBox);

      // automatically shift the h3 size based on zoom level,
      state.campaignRawAggSize = Math.min(6, Math.floor(action.payload.viewport.zoom) + 1);
    },
    setMapCoordinateInfo: (state, action: PayloadAction<{ mgrs?: string; latitude: number; longitude: number; zoom: number }>) => {
      state.mapCoordinateInfo = action.payload;
    },
    fitBoundsOnMapWithMapMarker: (state, action: PayloadAction<{ marker?: [number, number]; bounds: [number, number, number, number]; maxZoom?: number }>) => {
      const bounds = action.payload.bounds;
      state.fitBounds = bounds;
      state.fitBoundsImmediately = false;
      state.fitBoundsMaxZoom = action.payload.maxZoom;
      let marker = action.payload.marker;
      // if marker is not provided, use the center of the bounds:
      if (!marker && bounds.length === 4) {
        // bounds is [minLng, minLat, maxLng, maxLat]
        const [minLng, minLat, maxLng, maxLat] = bounds;
        marker = [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
      }
      state.fitBoundsMarker = marker;
    },
    fitBoundsOnMap: (state, action: PayloadAction<LngLatBoundsLike | { bounds: LngLatBoundsLike; maxZoom?: number }>) => {
      if ('bounds' in action.payload) {
        state.fitBounds = action.payload.bounds;
        state.fitBoundsMaxZoom = action.payload.maxZoom;
      } else {
        state.fitBounds = action.payload;
        state.fitBoundsMaxZoom = undefined;
      }
      state.fitBoundsImmediately = false;
    },
    clearFitBoundsMapMarker: (state) => {
      state.fitBoundsMarker = null;
    },
    flyToOnMap: (state, action: PayloadAction<MapViewport>) => {
      state.flyToViewport = action.payload;
    },
    fitBoundsComplete: (state) => {
      state.fitBounds = undefined;
      state.fitBoundsImmediately = false;
      state.fitBoundsMaxZoom = undefined;
      state.flyToViewport = null;
    },
    setThemeMode: (state, action: PayloadAction<PaletteMode>) => {
      state.themeMode = action.payload;
    },
    toggleEgeData: (state) => {
      state.isEgeDataOn = !state.isEgeDataOn;
    },
    setPolygonMode: (state, action: PayloadAction<PolygonMode>) => {
      state.polygonMode = action.payload;
    },
    toggleTouchstoneData: (state) => {
      // Toggle both polygon and EGE data together (simple on/off, no cycling)
      const isCurrentlyOn = state.polygonMode !== 'off' || state.isEgeDataOn;

      if (isCurrentlyOn) {
        // Turn both off
        state.polygonMode = 'off';
        state.isEgeDataOn = false;
      } else {
        // Turn both on
        state.polygonMode = 'global';
        state.isEgeDataOn = true;
      }
    },
    toggleWIFIData: (state) => {
      state.isWIFIDataOn = !state.isWIFIDataOn;
    },
    toggleCellData: (state) => {
      state.isCellDataOn = !state.isCellDataOn;
    },
    enableWifiData: (state) => {
      state.isWIFIDataOn = true;
    },
    enableCellData: (state) => {
      state.isCellDataOn = true;
    },
    enableSenpaiData: (state) => {
      state.senpaiDataOn = true;
    },
    toggleOffAllLayers: (state) => {
      state.polygonMode = 'off';
      state.isEgeDataOn = false;
      state.isWIFIDataOn = false;
      state.isCellDataOn = false;
      state.senpaiDataOn = false;
    },
    resetMap: (state) => {
      // Clear all layers
      state.polygonMode = 'off';
      state.isEgeDataOn = false;
      state.isWIFIDataOn = false;
      state.isCellDataOn = false;
      state.senpaiDataOn = false;

      // Reset basemap to default
      state.selectedBasemap = BASEMAPS[0];

      // Reset active page
      state.activePage = '/';

      // Close panel if open
      if (state.isPanelHeaderVisible) {
        state.isPanelHeaderVisible = false;
        state.panelHasBeenManuallyClosed = true;
      }

      // Jump to default view immediately (jumpTo, so no animation timing concern)
      state.flyToViewport = { latitude: 30, longitude: 0, zoom: 2 };

      state.detailPanelActiveTab = 'touchstone';

      clearFetchLayers(state);
    },
    selectBasemap: (state, action: PayloadAction<Basemap>) => {
      state.selectedBasemap = action.payload;
    },
    setSearchBarFilter: (state, action: PayloadAction<string>) => {
      state.searchBarFilter = action.payload;
    },
    toggleCampaignH3DwellLayer: (state, action: PayloadAction<boolean | undefined>) => {
      // if action.payload is set, use it, otherwise toggle the current state:
      if (action?.payload !== undefined) {
        state.campaignH3DwellLayerEnabled = action.payload;
      } else {
        state.campaignH3DwellLayerEnabled = !state.campaignH3DwellLayerEnabled;
      }
    },
    setDetailPanelActiveTab: (state, action: PayloadAction<ProductTab>) => {
      state.detailPanelActiveTab = action.payload;
      if (action.payload !== 'touchstone') clearSelectedEntityFlightJourneys(state);
    }
  }
});

const selectAudienceFilterMinPolygons = (store: RootState) => store.slice.audienceFilterMinPolygons;
const selectAudienceFilterMinScore = (store: RootState) => store.slice.audienceFilterMinScore;
const selectAudienceFilterEntityTypes = (store: RootState) => store.slice.audienceFilterEntityTypes;
const selectAudienceFilterFlightEvidenceTypes = (store: RootState) => store.slice.audienceFilterFlightEvidenceTypes;
const selectAudienceFilterFlightMilitaryTypes = (store: RootState) => store.slice.audienceFilterFlightMilitaryTypes;
const selectAudienceFilterVesselEvidenceTypes = (store: RootState) => store.slice.audienceFilterVesselEvidenceTypes;
const selectAudienceShowOnlySelections = (store: RootState) => store.slice.audienceShowOnlySelections;
const selectDetailPanelActiveTab = (store: RootState) => store.slice.detailPanelActiveTab;
const selectCampaignRawAggDimension = (store: RootState) => store.slice.campaignRawAggDimension;
const selectEgeRawDateRange = (store: RootState) => store.slice.egeRawDateRange;
const selectCampaignClusterDateRange = (store: RootState) => store.slice.campaignClusterDateRange;
const selectCampaignRawAggSize = (store: RootState) => store.slice.campaignRawAggSize;
const selectEventCountColorOverride = (store: RootState) => store.slice.eventCountColorOverride;
const selectDelayedMapBounds = (store: RootState) => store.slice.delayedMapBounds;
const selectDelayedMapBoundsBox = (store: RootState) => store.slice.delayedMapBoundsBox;
const selectDelayedMapBoundsWKT = (store: RootState) => store.slice.delayedMapBoundsWKT;
const selectIsPanelHeaderVisible = (store: RootState) => store.slice.isPanelHeaderVisible;
const selectDelayedMapViewport = (store: RootState) => store.slice.delayedMapViewport;
const selectDetailsAudienceDateRange = (store: RootState) => store.slice.detailsAudienceDateRange;
const selectFlyToViewport = (store: RootState) => store.slice.flyToViewport;
const selectFitBounds = (store: RootState) => store.slice.fitBounds;
const selectFitBoundsImmediately = (store: RootState) => store.slice.fitBoundsImmediately;
const selectFitBoundsMaxZoom = (store: RootState) => store.slice.fitBoundsMaxZoom;
const selectFitBoundsMarker = (store: RootState) => store.slice.fitBoundsMarker;
const selectInitialMapViewport = (store: RootState) => store.slice.initialMapViewport;
const selectIsEgeDataOn = (store: RootState) => store.slice.isEgeDataOn;
const selectPolygonMode = (store: RootState) => store.slice.polygonMode;
const selectIsWIFIDataOn = (store: RootState) => store.slice.isWIFIDataOn;
const selectIsCellDataOn = (store: RootState) => store.slice.isCellDataOn;
const selectSenpaiDataOn = (store: RootState) => store.slice.senpaiDataOn;
const selectSenpaiFilterMinRfiCount = (store: RootState) => store.slice.senpaiFilterMinRfiCount;
const selectSenpaiSortField = (store: RootState) => store.slice.senpaiSortField;
const selectSenpaiSortOrder = (store: RootState) => store.slice.senpaiSortOrder;
const selectDetailsSenpai = (store: RootState) => store.slice.detailsSenpai;
const selectSelectedSenpaiList = (store: RootState) => store.slice.selectedSenpaiList;
const selectSelectedSenpaiLookup = (store: RootState) => store.slice.selectedSenpaiLookup;
const selectDetailsSenpaiIdx = (store: RootState) => store.slice.detailsSenpaiIdx;
const selectSenpaiShowOnlySelections = (store: RootState) => store.slice.senpaiShowOnlySelections;
const selectDataminrFilterAlertTypes = (store: RootState) => store.slice.dataminrFilterAlertTypes;
const selectDataminrSortField = (store: RootState) => store.slice.dataminrSortField;
const selectDataminrSortOrder = (store: RootState) => store.slice.dataminrSortOrder;
const selectDetailsDataminr = (store: RootState) => store.slice.detailsDataminr;
const selectSelectedDataminrList = (store: RootState) => store.slice.selectedDataminrList;
const selectSelectedDataminrLookup = (store: RootState) => store.slice.selectedDataminrLookup;
const selectDetailsDataminrIdx = (store: RootState) => store.slice.detailsDataminrIdx;
const selectDataminrShowOnlySelections = (store: RootState) => store.slice.dataminrShowOnlySelections;
const selectDataminrDataOn = (store: RootState) => store.slice.dataminrDataOn;
const selectAriDateRange = (store: RootState) => store.filters.ariDateRange;
const selectLocationFilterAdvertiserIds = (store: RootState) => store.slice.locationFilterAdvertiserIds;
const selectLocationFilterCategory = (store: RootState) => store.slice.locationFilterCategory;
const selectLocationFilterCountry = (store: RootState) => store.slice.locationFilterCountry;
const selectLocationFilterMinUniqueEntities = (store: RootState) => store.slice.locationFilterMinUniqueEntities;
const selectNumLocationsSelected = (store: RootState) => store.slice.selectedLocationIds.length;
const selectIsLocationNetworkEnabled = (store: RootState) => store.slice.isLocationNetworkEnabled;
const selectLocationNetworkPolygonId = (store: RootState) => store.slice.locationNetworkPolygonId;
const selectLocationShowOnlySelections = (store: RootState) => store.slice.locationShowOnlySelections;
const selectSearchBarFilter = (store: RootState) => store.slice.searchBarFilter;
const selectSelectedAudience = (store: RootState) => store.slice.selectedAudience;
const selectSelectedAudienceColorOverride = (state: RootState) => state.slice.selectedAudienceColorOverride;
const selectCampaignEntityColors = (state: RootState) => state.slice.campaignEntityColors;
const selectSelectedAudienceIds = (store: RootState) => store.slice.selectedAudienceIds;
const selectSelectedLocationIds = (store: RootState) => store.slice.selectedLocationIds;
const selectSelectedLocationsLookup = (store: RootState) => store.slice.selectedLocationsLookup;
const selectSelectedLocations = (store: RootState) => store.slice.selectedLocations;
const selectSelectedLocationAttrPanel = (store: RootState) => store.slice.selectedLocationAttrPanel;
const selectSlice = (store: RootState) => JSON.stringify(store.slice);
const selectSortAudienceBy = (store: RootState) => store.slice.sortAudienceBy;
const selectAudienceSortOrder = (store: RootState) => store.slice.audienceSortOrder;
const selectSortLocationsBy = (store: RootState) => store.slice.sortLocationsBy;
const selectLocationSortOrder = (store: RootState) => store.slice.locationSortOrder;
const selectUser = (store: RootState) => store.slice.user;
const selectCampaignCreatorOpen = (store: RootState) => store.slice.campaignCreatorOpen;
const selectCampaign = (store: RootState) => store.slice.campaign;
const selectDetailsAudience = (store: RootState) => store.slice.detailsAudience;
const selectLocationDetails = (store: RootState) => store.slice.detailsLocation;
const selectLocationDetailsActiveTab = (store: RootState) => store.slice.detailsLocationActiveTab;
const selectDrawToolActive = (store: RootState) => store.slice.drawToolActive;
const selectMeasurementActive = (store: RootState) => store.slice.measurementActive;
const selectSelectedBasemap = (store: RootState) => store.slice.selectedBasemap;
const selectCampaignH3DwellLayerEnabled = (store: RootState) => store.slice.campaignH3DwellLayerEnabled;
const selectCampaignH3DwellLayerDate = (store: RootState) => store.slice.campaignH3DwellLayerDate;
const selectCampaignH3DwellLayerRes = (store: RootState) => store.slice.campaignH3DwellLayerRes;
const selectCampaignH3DwellLayerDim = (store: RootState) => store.slice.campaignH3DwellLayerDim;
const selectCampaignH3DwellLayerScaleFilter = (store: RootState) => store.slice.campaignH3DwellLayerScaleFilter;
const selectCampaignOnScreenEntities = (store: RootState) => store.slice.campaignOnScreenEntities;
const selectSelectedAudienceLookup = (store: RootState) => store.slice.selectedAudienceLookup;
const selectCampaignH3DwellLayerScaleType = (store: RootState) => store.slice.campaignH3DwellLayerScaleType;
const selectShareCampaign = (store: RootState) => store.slice.shareCampaign;
const selectEgeRawPlaying = (store: RootState) => store.slice.egeRawPlaying;
const selectDrawToolSelected = (store: RootState) => store.slice.drawToolSelected;
const selectCampaignTimelineRange = (store: RootState) => store.slice.campaignTimelineRange;
const selectCampaignFilterEndDate = (store: RootState) => store.slice.campaignFilterEndDate;
const selectCampaignFilterTouchstoneStartDate = (store: RootState) => store.slice.campaignFilterTouchstoneStartDate;
const selectCampaignFilterTouchstoneEndDate = (store: RootState) => store.slice.campaignFilterTouchstoneEndDate;
const selectCampaignFilterEdgeStartDate = (store: RootState) => store.slice.campaignFilterEdgeStartDate;
const selectCampaignFilterEdgeEndDate = (store: RootState) => store.slice.campaignFilterEdgeEndDate;
const selectCampaignFilterContinuousCampaign = (store: RootState) => store.slice.campaignFilterContinuousCampaign;
const selectCampaignFilterMinScore = (store: RootState) => store.slice.campaignFilterMinScore;
const selectCampaignFilterShowTouchstoneChips = (store: RootState) => store.slice.campaignFilterShowTouchstoneChips;
const selectEgeLayerRequests = (store: RootState) => store.slice.egeLayerRequests;
// Flight and vessel evidence follow the dates of the CTD fetch (or the whole campaign) on the map.
// The campaign timeline only controls what the map shows, so it never narrows them.
export const selectAudienceTravelDateRange = createSelector(
  [selectEgeLayerRequests, selectCampaign, selectCampaignFilterTouchstoneStartDate, selectCampaignFilterTouchstoneEndDate],
  (layers, campaign, start, end) => getAudienceTravelDateRange(layers ?? [], campaign, start, end)
);
const selectActivePage = (store: RootState) => store.slice.activePage;
const selectWifiResults = (store: RootState) => store.slice.wifiResults;
const selectWifiConnectedOrSurveyedLoading = (store: RootState) => store.slice.wifiConnectedOrSurveyedLoading;

// Tacklebox selectors
const selectTackleboxAdminLayer = (store: RootState) => store.slice.tackleboxAdminLayer;
const selectTackleboxSlots = (store: RootState) => store.slice.tackleboxSlots;
const selectTackleboxDrawnPolygonSelected = (store: RootState) => store.slice.tackleboxDrawnPolygonSelected;
const selectTackleboxDrawModeEnabled = (store: RootState) => store.slice.tackleboxDrawModeEnabled;
const selectTackleboxDrawingSlotId = (store: RootState) => store.slice.tackleboxDrawingSlotId;
const selectTackleboxJobName = (store: RootState) => store.slice.tackleboxJobName;
const selectTackleboxMinTitoPolygons = (store: RootState) => store.slice.tackleboxMinTitoPolygons;
const selectTackleboxLoadedJobId = (store: RootState) => store.slice.tackleboxLoadedJobId;

const selectTackleboxPolygons = createSelector([selectTackleboxSlots], (slots) => slots.filter((s) => s.polygon != null).map((s) => s.polygon as Feature));

const snapshot = (store: RootState) => {
  // Create a deep copy to avoid mutating the original state
  const outputSnapshot = {
    slice: structuredClone({ ...store.slice, wifiResults: [], selectedEntityFlightJourneys: [], selectedEntityVesselJourneys: [] }),
    filters: structuredClone(store.filters),
    pathname: window.location.pathname
  };
  return outputSnapshot;
};

const alertsModelSelector = createSelector([selectUser], (user) => ({
  user
}));

// Used by the Map Component:
const mapComponentSelector = createSelector(
  [
    selectActivePage,
    selectFlyToViewport,
    selectFitBounds,
    selectFitBoundsImmediately,
    selectFitBoundsMaxZoom,
    selectFitBoundsMarker,
    selectInitialMapViewport,
    selectDelayedMapViewport,
    selectDelayedMapBounds,
    selectDelayedMapBoundsWKT,
    selectSelectedBasemap,
    selectCampaign,
    selectDrawToolActive,
    selectDrawToolSelected,
    selectMeasurementActive,
    selectCampaignRawAggSize,
    selectCampaignH3DwellLayerRes,
    selectCampaignH3DwellLayerDim,
    selectCampaignH3DwellLayerScaleFilter,
    selectCampaignH3DwellLayerScaleType,
    selectSenpaiDataOn,
    selectDetailPanelActiveTab,
    (store: RootState) => store.slice.tackleboxDrawModeEnabled
  ],
  (
    // \n
    activePage,
    flyToViewport,
    fitBounds,
    fitBoundsImmediately,
    fitBoundsMaxZoom,
    fitBoundsMarker,
    initialMapViewport,
    delayedMapViewport,
    delayedMapBounds,
    delayedMapBoundsWKT,
    selectedBasemap,
    campaign,
    drawToolActive,
    drawToolSelected,
    measurementActive,
    campaignRawAggSize,
    campaignH3DwellLayerRes,
    campaignH3DwellLayerDim,
    campaignH3DwellLayerScaleFilter,
    campaignH3DwellLayerScaleType,
    senpaiDataOn,
    detailPanelActiveTab,
    tackleboxDrawModeEnabled
  ) => ({
    activePage,
    flyToViewport,
    fitBounds,
    fitBoundsImmediately,
    fitBoundsMaxZoom,
    fitBoundsMarker,
    initialMapViewport,
    delayedMapViewport,
    delayedMapBounds,
    delayedMapBoundsWKT,
    selectedBasemap,
    campaign,
    drawToolActive,
    drawToolSelected,
    measurementActive,
    campaignRawAggSize,
    campaignH3DwellLayerRes,
    campaignH3DwellLayerDim,
    campaignH3DwellLayerScaleFilter,
    campaignH3DwellLayerScaleType,
    senpaiDataOn,
    detailPanelActiveTab,
    tackleboxDrawModeEnabled
  })
);
// Used by AppView.tsx
const appComponentSelector = createSelector(
  [selectUser, selectDelayedMapBounds, selectCampaign, selectSenpaiDataOn, selectDelayedMapBoundsWKT, selectIsPanelHeaderVisible],
  (user, delayedMapBounds, campaign, senpaiDataOn, delayedMapBoundsWKT, isPanelHeaderVisible) => ({
    user,
    delayedMapBounds,
    campaign,
    senpaiDataOn,
    delayedMapBoundsWKT,
    isPanelHeaderVisible
  })
);

// Used by Map/index.tsx for additional state
const mapDataLayerSelector = createSelector(
  [
    selectSenpaiDataOn,
    selectWifiConnectedOrSurveyedLoading,
    selectIsPanelHeaderVisible,
    selectWifiResults,
    selectIsWIFIDataOn,
    selectPolygonMode,
    selectEgeLayerRequests,
    selectAriDateRange,
    selectSenpaiFilterMinRfiCount,
    selectDataminrDataOn
  ],
  (
    senpaiDataOn,
    wifiConnectedOrSurveyedLoading,
    isPanelHeaderVisible,
    wifiResults,
    isWIFIDataOn,
    polygonMode,
    egeLayerRequests,
    ariDateRange,
    minRfiCount,
    dataminrDataOn
  ) => ({
    senpaiDataOn,
    dataminrDataOn,
    wifiConnectedOrSurveyedLoading,
    isPanelHeaderVisible,
    wifiResults,
    isWIFIDataOn,
    polygonMode,
    egeLayerRequests,
    ariDateRange,
    minRfiCount
  })
);

const senpaiResultPanelSelector = createSelector(
  [selectDelayedMapBoundsWKT, selectDelayedMapViewport, selectSelectedSenpaiList, selectSenpaiShowOnlySelections],
  (delayedMapBoundsWKT, delayedMapViewport, selectedSenpaiList, senpaiShowOnlySelections) => ({
    delayedMapBoundsWKT,
    delayedMapZoom: delayedMapViewport?.zoom,
    selectedSenpaiList,
    senpaiShowOnlySelections
  })
);

const senpaiDetailPanelSelector = createSelector(
  [selectDetailsSenpai, selectSelectedSenpaiList, selectSelectedSenpaiLookup, selectDetailsSenpaiIdx, (store: RootState) => store.slice.detailsSenpaiActiveTab],
  (detailsSenpai, selectedSenpaiList, selectedSenpaiLookup, detailsSenpaiIdx, detailsSenpaiActiveTab) => ({
    detailsSenpai,
    selectedSenpaiList,
    selectedSenpaiLookup,
    detailsSenpaiIdx,
    detailsSenpaiActiveTab
  })
);

const senpaiFiltersSelector = createSelector([selectSenpaiFilterMinRfiCount, selectSenpaiSortField, selectSenpaiSortOrder], (minRfiCount, sortField, sortOrder) => ({
  minRfiCount,
  sortField,
  sortOrder
}));

const dataminrResultPanelSelector = createSelector(
  [selectDelayedMapBoundsWKT, selectDelayedMapViewport, selectSelectedDataminrList, selectDataminrShowOnlySelections],
  (delayedMapBoundsWKT, delayedMapViewport, selectedDataminrList, dataminrShowOnlySelections) => ({
    delayedMapBoundsWKT,
    delayedMapZoom: delayedMapViewport?.zoom,
    selectedDataminrList,
    dataminrShowOnlySelections
  })
);

const dataminrDetailPanelSelector = createSelector(
  [
    selectDetailsDataminr,
    selectSelectedDataminrList,
    selectSelectedDataminrLookup,
    selectDetailsDataminrIdx,
    (store: RootState) => store.slice.detailsDataminrActiveTab,
    (store: RootState) => store.slice.dataminrDetailCollapsed
  ],
  (detailsDataminr, selectedDataminrList, selectedDataminrLookup, detailsDataminrIdx, detailsDataminrActiveTab, dataminrDetailCollapsed) => ({
    detailsDataminr,
    selectedDataminrList,
    selectedDataminrLookup,
    detailsDataminrIdx,
    detailsDataminrActiveTab,
    dataminrDetailCollapsed
  })
);

const dataminrFiltersSelector = createSelector(
  [selectDataminrFilterAlertTypes, selectDataminrSortField, selectDataminrSortOrder],
  (alertTypes, sortField, sortOrder) => ({
    alertTypes,
    sortField,
    sortOrder
  })
);

const audienceV2ListSelector = createSelector([selectSelectedLocationIds, selectSelectedAudienceColorOverride], (selectedLocationIds, selectedAudienceColorOverride) => ({
  selectedLocationIds,
  selectedAudienceColorOverride
}));

const saveStateSelector = createSelector([selectSlice], (slice) => ({
  slice
}));

const panelHeaderSelector = createSelector([selectSearchBarFilter], (searchBarFilter) => ({
  searchBarFilter
}));

// selectedLocationsLookup, selectedAudienceColorOverride
const entityDetailsSelector = createSelector([selectDetailsAudienceDateRange, selectSelectedLocations], (detailsAudienceDateRange, selectedLocations) => ({
  detailsAudienceDateRange,
  selectedLocations
}));

const useGetLocationQueryHookSelector = createSelector(
  [
    selectDelayedMapBounds,
    selectLocationFilterCategory,
    selectLocationFilterCountry,
    selectLocationFilterMinUniqueEntities,
    selectLocationFilterAdvertiserIds,
    selectCampaign,
    selectDelayedMapViewport,
    selectPolygonMode
  ],
  (bounding_box, categories, countries, min_unique_entities, advertiserIds, campaign, delayedMapViewport, polygonMode) => ({
    categories,
    countries,
    bounding_box,
    min_unique_entities,
    advertiser_ids: advertiserIds.length > 0 ? advertiserIds : undefined,
    campaign: polygonMode === 'campaign' && campaign ? campaign : undefined,
    mapZoom: delayedMapViewport?.zoom,
    polygonMode
  })
);

const polygonMapLayerSelector = createSelector(
  [selectSelectedLocationIds, selectPolygonMode, selectLocationShowOnlySelections],
  (selectedLocationIds, polygonMode, locationShowOnlySelections) => ({
    selectedLocationIds,
    polygonMode,
    locationShowOnlySelections
  })
);

const useEGESelector = createSelector(
  [selectDelayedMapBoundsBox, selectDelayedMapViewport, selectDelayedMapBoundsWKT, selectIsEgeDataOn],
  (delayedMapBoundsBox, delayedMapViewport, delayedMapBoundsWKT, isEgeDataOn) => ({
    delayedMapBoundsBox,
    delayedMapZoom: delayedMapViewport?.zoom,
    delayedMapBoundsWKT,
    isEgeDataOn
  })
);

// provide a stable object for the `useSelector` hook to prevent unnecessary re-renders:
const useEGESelectorFilters = createSelector([selectSelectedAudienceIds], (selectedAudienceIds): EGEDataRequestFilters => ({
  selectedAudienceIds
}));

const locationFiltersSelector = createSelector(
  [selectLocationFilterCategory, selectLocationFilterCountry, selectLocationFilterMinUniqueEntities, selectLocationFilterAdvertiserIds],
  (locationFilterCategory, locationFilterCountry, locationFilterMinUniqueEntities, locationFilterAdvertiserIds) => ({
    locationFilterCategory,
    locationFilterCountry,
    locationFilterMinUniqueEntities,
    locationFilterAdvertiserIds
  })
);

const locationNetworkSelector = createSelector(
  [selectIsLocationNetworkEnabled, selectLocationNetworkPolygonId],
  (isLocationNetworkEnabled, locationNetworkPolygonId) => ({
    isLocationNetworkEnabled,
    locationNetworkPolygonId
  })
);

const layerSwitcherSelector = createSelector(
  [selectSelectedBasemap, selectIsLocationNetworkEnabled, selectLocationNetworkPolygonId],
  (selectedBasemap, isLocationNetworkEnabled, locationNetworkPolygonId) => ({
    selectedBasemap,
    isLocationNetworkEnabled,
    locationNetworkPolygonId
  })
);

const layerSwitcherFlycasterAnalyticLayersSelector = createSelector([selectCampaign, selectCampaignH3DwellLayerEnabled], (campaign, campaignH3DwellLayerEnabled) => ({
  campaignH3DwellLayerEnabled,
  campaign
}));

const locationV2Selector = createSelector(
  [selectSelectedLocations, selectLocationShowOnlySelections, selectEgeLayerRequests, selectSortLocationsBy, selectLocationSortOrder, selectCampaign],
  (selectedLocations, locationShowOnlySelections, egeLayerRequests, sortLocationsBy, locationSortOrder, campaign) => ({
    selectedLocations,
    locationShowOnlySelections,
    egeLayerRequests,
    sortLocationsBy,
    locationSortOrder,
    campaign
  })
);

const locationV2MoreOptionsSelector = createSelector(
  [selectSortLocationsBy, selectLocationSortOrder, selectSelectedLocations],
  (sortLocationsBy, locationSortOrder, selectedLocations) => ({
    sortLocationsBy,
    locationSortOrder,
    selectedLocations
  })
);

const audienceV2FilterPopoverSelector = createSelector(
  [
    selectAudienceFilterMinPolygons,
    selectAudienceFilterMinScore,
    selectAudienceFilterEntityTypes,
    selectAudienceFilterFlightEvidenceTypes,
    selectAudienceFilterFlightMilitaryTypes,
    selectAudienceFilterVesselEvidenceTypes
  ],
  (
    audienceFilterMinPolygons,
    audienceFilterMinScore,
    audienceFilterEntityTypes,
    audienceFilterFlightEvidenceTypes,
    audienceFilterFlightMilitaryTypes,
    audienceFilterVesselEvidenceTypes
  ) => ({
    audienceFilterMinPolygons,
    audienceFilterMinScore,
    audienceFilterEntityTypes,
    audienceFilterFlightEvidenceTypes,
    audienceFilterFlightMilitaryTypes,
    audienceFilterVesselEvidenceTypes
  })
);
const ifReceiverSelector = createSelector([selectSelectedAudienceIds, selectSelectedLocationIds], (selectedAudienceIds, selectedLocationIds) => ({
  selectedAudienceIds,
  selectedLocationIds
}));

const campaignCreatorSelector = createSelector(
  [
    selectCampaignCreatorOpen,
    selectSelectedAudienceIds,
    selectSelectedAudienceLookup,
    selectSelectedLocationIds,
    selectCampaignFilterEndDate,
    selectCampaignFilterTouchstoneStartDate,
    selectCampaignFilterTouchstoneEndDate,
    selectCampaignFilterEdgeStartDate,
    selectCampaignFilterEdgeEndDate,
    selectCampaignFilterContinuousCampaign,
    selectCampaignFilterMinScore,
    selectCampaignFilterShowTouchstoneChips
  ],
  (
    campaignCreatorOpen,
    selectedAudienceIds,
    selectedAudienceLookup,
    selectedLocationIds,
    campaignFilterEndDate,
    campaignFilterTouchstoneStartDate,
    campaignFilterTouchstoneEndDate,
    campaignFilterEdgeStartDate,
    campaignFilterEdgeEndDate,
    campaignFilterContinuousCampaign,
    campaignFilterMinScore,
    campaignFilterShowTouchstoneChips
  ) => ({
    campaignCreatorOpen,
    selectedAudienceIds,
    selectedAudienceLookup,
    selectedLocationIds,
    campaignFilterEndDate,
    campaignFilterTouchstoneStartDate,
    campaignFilterTouchstoneEndDate,
    campaignFilterEdgeStartDate,
    campaignFilterEdgeEndDate,
    campaignFilterContinuousCampaign,
    campaignFilterMinScore,
    campaignFilterShowTouchstoneChips
  })
);

const campaignFilterSelector = createSelector(
  [
    selectCampaignFilterEndDate,
    selectCampaignFilterTouchstoneStartDate,
    selectCampaignFilterTouchstoneEndDate,
    selectCampaignFilterEdgeStartDate,
    selectCampaignFilterEdgeEndDate,
    selectCampaignFilterContinuousCampaign,
    selectCampaignFilterMinScore,
    selectCampaignFilterShowTouchstoneChips
  ],
  (
    campaignFilterEndDate,
    campaignFilterTouchstoneStartDate,
    campaignFilterTouchstoneEndDate,
    campaignFilterEdgeStartDate,
    campaignFilterEdgeEndDate,
    campaignFilterContinuousCampaign,
    campaignFilterMinScore,
    campaignFilterShowTouchstoneChips
  ) => ({
    campaignFilterEndDate,
    campaignFilterTouchstoneStartDate,
    campaignFilterTouchstoneEndDate,
    campaignFilterEdgeStartDate,
    campaignFilterEdgeEndDate,
    campaignFilterContinuousCampaign,
    campaignFilterMinScore,
    campaignFilterShowTouchstoneChips
  })
);

const useRawLayerSelectorBounds = createSelector(
  [
    selectSelectedAudienceColorOverride,
    selectCampaignEntityColors,
    selectCampaign,
    selectSelectedAudienceIds,
    selectDelayedMapBoundsWKT,
    selectIsEgeDataOn,
    selectDelayedMapViewport,
    selectCampaignRawAggDimension,
    selectCampaignRawAggSize,
    selectEgeRawDateRange,
    selectAudienceShowOnlySelections,
    selectSelectedAudienceLookup
  ],
  (
    selectedAudienceColorOverride,
    campaignEntityColors,
    campaign,
    selectedAudienceIds,
    delayedMapBoundsWKT,
    isEgeDataOn,
    delayedMapViewport,
    campaignRawAggDimension,
    campaignRawAggSize,
    egeRawDateRange,
    audienceShowOnlySelections,
    selectedAudienceLookup
  ) => ({
    selectedAudienceColorOverride,
    campaignEntityColors,
    campaign,
    selectedAudienceIds,
    delayedMapBoundsWKT,
    isEgeDataOn,
    delayedMapZoom: delayedMapViewport?.zoom,
    campaignRawAggDimension,
    campaignRawAggSize,
    egeRawDateRange,
    audienceShowOnlySelections,
    selectedAudienceLookup
  })
);

const useRawLayerSelector = createSelector(
  [
    selectSelectedAudienceColorOverride,
    selectCampaign,
    selectSelectedAudienceIds,
    // selectDelayedMapBoundsWKT,
    selectIsEgeDataOn,
    selectDelayedMapViewport,
    selectCampaignRawAggDimension,
    selectCampaignRawAggSize,
    selectEgeRawDateRange,
    selectAudienceShowOnlySelections,
    selectEventCountColorOverride
  ],
  (
    selectedAudienceColorOverride,
    campaign,
    selectedAudienceIds,
    // delayedMapBoundsWKT,
    isEgeDataOn,
    delayedMapViewport,
    campaignRawAggDimension,
    campaignRawAggSize,
    egeRawDateRange,
    audienceShowOnlySelections,
    eventCountColorOverride
  ) => ({
    selectedAudienceColorOverride,
    campaign,
    selectedAudienceIds,
    // delayedMapBoundsWKT,
    isEgeDataOn,
    delayedMapZoom: delayedMapViewport?.zoom,
    campaignRawAggDimension,
    campaignRawAggSize,
    egeRawDateRange,
    audienceShowOnlySelections,
    eventCountColorOverride
  })
);

const audienceV2ShowOnlySelectedButtonSelector = createSelector([selectAudienceShowOnlySelections], (audienceShowOnlySelections) => ({
  audienceShowOnlySelections
}));

const locationV2ShowOnlySelectedButtonSelector = createSelector([selectLocationShowOnlySelections], (locationShowOnlySelections) => ({
  locationShowOnlySelections
}));

const campaignHeaderSelector = createSelector(
  [selectSelectedAudienceIds, selectDelayedMapBoundsWKT, selectIsEgeDataOn, selectDelayedMapViewport, selectCampaignRawAggDimension, selectCampaignRawAggSize],
  (selectedAudienceIds, delayedMapBoundsWKT, isEgeDataOn, delayedMapViewport, campaignRawAggDimension, campaignRawAggSize) => ({
    selectedAudienceIds,
    delayedMapBoundsWKT,
    isEgeDataOn,
    delayedMapZoom: delayedMapViewport?.zoom,
    campaignRawAggDimension,
    campaignRawAggSize
  })
);

const campaignPresetsPopoverSelector = createSelector([selectCampaignTimelineRange, selectCampaign], (campaignTimelineRange, campaign) => ({
  campaignTimelineRange,
  campaign
}));

const audienceV2TimelineChartSelector = createSelector(
  [selectSelectedAudienceIds, selectCampaign, selectEgeRawDateRange, selectDelayedMapViewport],
  (selectedAudienceIds, campaign, egeRawDateRange) => ({
    selectedAudienceIds,
    campaign,
    egeRawDateRange
  })
);

const audienceV2DetailsSelector = createSelector(
  [selectSelectedAudienceIds, selectDetailsAudience, selectCampaign],
  (selectedAudienceIds, detailsAudience, campaign) => ({
    selectedAudienceIds,
    detailsAudience,
    campaign
  })
);

const AudienceV2MultiDetailsSelector = createSelector([selectSelectedAudienceIds, selectCampaign], (selectedAudienceIds, campaign) => ({
  selectedAudienceIds,
  campaign
}));

const locationV2DetailsSelector = createSelector([selectLocationDetails, selectLocationDetailsActiveTab], (locationDetails, detailsLocationActiveTab) => ({
  locationDetails,
  detailsLocationActiveTab
}));

const useClusterLayerSelector = createSelector(
  [selectCampaign, selectDelayedMapBoundsWKT, selectCampaignClusterDateRange],
  (campaign, delayedMapBoundsWKT, campaignClusterDateRange) => ({
    campaign,
    delayedMapBoundsWKT,
    campaignClusterDateRange
  })
);

const useH3DwellLayerSelector = createSelector(
  [
    selectCampaign,
    selectCampaignH3DwellLayerEnabled,
    // selectDelayedMapBoundsWKT,
    selectCampaignH3DwellLayerDate,
    selectSelectedAudienceIds,
    selectCampaignH3DwellLayerRes,
    selectCampaignH3DwellLayerDim,
    selectCampaignH3DwellLayerScaleFilter,
    selectCampaignH3DwellLayerScaleType
  ],
  (
    campaign,
    campaignH3DwellLayerEnabled,
    // delayedMapBoundsWKT,
    campaignH3DwellDate,
    selectedAudienceIds,
    campaignH3DwellLayerRes,
    campaignH3DwellLayerDim,
    campaignH3DwellLayerScaleFilter,
    campaignH3DwellLayerScaleType
  ) => ({
    campaign,
    campaignH3DwellLayerEnabled,
    // delayedMapBoundsWKT,
    campaignH3DwellDate,
    selectedAudienceIds,
    campaignH3DwellLayerRes,
    campaignH3DwellLayerDim,
    campaignH3DwellLayerScaleFilter,
    campaignH3DwellLayerScaleType
  })
);

const audienceV2SortPopoverSelector = createSelector(
  [selectNumLocationsSelected, selectSortAudienceBy, selectAudienceSortOrder, selectSelectedAudience, selectSelectedLocations, selectEgeLayerRequests],
  (numLocationsSelected, sortAudienceBy, audienceSortOrder, selectedAudience, selectedLocations, egeLayerRequests) => ({
    numLocationsSelected,
    sortAudienceBy,
    audienceSortOrder,
    selectedAudience,
    selectedLocations,
    egeLayerRequests
  })
);

const entityTimelineSelector = createSelector(
  [selectDetailsAudience, selectDetailsAudienceDateRange, selectSelectedLocationAttrPanel],
  (detailsAudience, detailsAudienceDateRange, selectedLocationAttrPanel) => ({
    detailsAudience,
    detailsAudienceDateRange,
    selectedLocationAttrPanel
  })
);

const audienceV2DetailsAttrBreakoutSelector = createSelector(
  [selectSelectedLocationsLookup, selectSelectedLocationAttrPanel, selectCampaign],
  (selectedLocationsLookup, selectedLocationAttrPanel, selectedCampaign) => ({
    selectedLocationsLookup,
    selectedLocationAttrPanel,
    selectedCampaign
  })
);

const rawTimelineSelector = createSelector(
  [selectEgeRawDateRange, selectCampaignH3DwellLayerEnabled, selectEgeRawPlaying],
  (egeRawDateRange, campaignH3DwellLayerEnabled, egeRawPlaying) => ({
    egeRawDateRange,
    campaignH3DwellLayerEnabled,
    egeRawPlaying
  })
);

const ariTimelineSelector = createSelector([selectAriDateRange], (ariDateRange) => ({
  ariDateRange
}));

const dwellTimelineSelector = createSelector(
  [selectCampaignH3DwellLayerEnabled, selectCampaignH3DwellLayerDate],
  (campaignH3DwellLayerEnabled, campaignH3DwellLayerDate) => ({
    campaignH3DwellLayerEnabled,
    campaignH3DwellLayerDate
  })
);

const audienceV2CardSelector = createSelector(
  [selectSelectedAudienceLookup, selectCampaignOnScreenEntities, selectAudienceShowOnlySelections, selectDetailsAudience],
  (selectedAudienceLookup, campaignOnScreenEntities, audienceShowOnlySelections, audienceDetails) => ({
    selectedAudienceLookup,
    campaignOnScreenEntities,
    audienceShowOnlySelections,
    audienceDetails
  })
);
const shareCampaignSelector = createSelector([selectCampaignCreatorOpen, selectShareCampaign], (campaignCreatorOpen, sharingCampaign) => ({
  campaignCreatorOpen,
  sharingCampaign
}));

const timelineSelector = createSelector(
  [selectEgeRawDateRange, selectCampaignH3DwellLayerDim, selectAriDateRange],
  (egeRawDateRange, campaignH3DwellLayerDim, ariDateRange) => ({
    egeRawDateRange,
    campaignH3DwellLayerDim,
    ariDateRange
  })
);

const drawButtonSelector = createSelector([selectDrawToolSelected, selectDrawToolActive, selectCampaign], (drawToolSelected, drawToolActive, campaign) => ({
  drawToolSelected,
  drawToolActive,
  campaign
}));

const layersPopoverSelector = createSelector(
  [selectPolygonMode, selectIsEgeDataOn, selectIsWIFIDataOn, selectIsCellDataOn],
  (polygonMode, isEgeDataOn, isWIFIDataOn, isCellDataOn) => ({
    polygonMode,
    isEgeDataOn,
    isWIFIDataOn,
    isCellDataOn
  })
);

const locationV2CardSetSelector = createSelector([selectSelectedLocationsLookup, selectLocationDetails], (selectedLocationsLookup, locationDetails) => ({
  selectedLocationsLookup,
  locationDetails
}));

const shareButtonSaveModalSelector = createSelector([selectCampaign, selectDelayedMapViewport], (campaign, delayedMapViewport) => ({
  campaign,
  delayedMapViewport
}));

const tackleboxStateForTool = createSelector(
  [
    selectTackleboxPolygons,
    selectTackleboxJobName,
    selectTackleboxDrawModeEnabled,
    selectTackleboxDrawnPolygonSelected,
    selectTackleboxAdminLayer,
    selectTackleboxMinTitoPolygons,
    selectTackleboxSlots,
    selectTackleboxDrawingSlotId
  ],
  (polygons, jobName, drawModeEnabled, drawnPolygonSelected, adminLayer, minTitoPolygons, slots, drawingSlotId) => ({
    polygons,
    jobName,
    drawModeEnabled,
    drawnPolygonSelected,
    adminLayer,
    minTitoPolygons,
    slots,
    drawingSlotId
  })
);

const tackleboxStateForHistory = createSelector([selectTackleboxLoadedJobId], (loadedJobId) => ({
  loadedJobId
}));

const tackleboxStateForMap = createSelector(
  [selectTackleboxAdminLayer, selectTackleboxPolygons, selectTackleboxDrawModeEnabled],
  (tackleboxAdminLayer, tackleboxPolygons, tackleboxDrawModeEnabled) => ({
    tackleboxAdminLayer,
    tackleboxPolygons,
    tackleboxDrawModeEnabled
  })
);

export const sliceSelectors = {
  locationV2CardSetSelector,
  shareButtonSaveModalSelector,
  senpaiResultPanelSelector,
  senpaiDetailPanelSelector,
  senpaiFiltersSelector,
  dataminrResultPanelSelector,
  dataminrDetailPanelSelector,
  dataminrFiltersSelector,
  selectDetailsSenpai,
  campaignFilterSelector,
  snapshot,
  timelineSelector,
  shareCampaignSelector,
  dwellTimelineSelector,
  rawTimelineSelector,
  ariTimelineSelector,
  audienceV2DetailsAttrBreakoutSelector,
  entityTimelineSelector,
  audienceV2SortPopoverSelector,
  useClusterLayerSelector,
  useH3DwellLayerSelector,
  locationV2DetailsSelector,
  audienceV2DetailsSelector,
  AudienceV2MultiDetailsSelector,
  audienceV2TimelineChartSelector,
  campaignHeaderSelector,
  campaignPresetsPopoverSelector,
  useRawLayerSelector,
  useRawLayerSelectorBounds,
  mapComponentSelector,
  appComponentSelector,
  saveStateSelector,
  alertsModelSelector,
  panelHeaderSelector,
  entityDetailsSelector,
  useEGESelector,
  useEGESelectorFilters,
  useGetLocationQueryHookSelector,
  polygonMapLayerSelector,
  audienceV2ListSelector,
  locationFiltersSelector,
  locationNetworkSelector,
  layerSwitcherSelector,
  layerSwitcherFlycasterAnalyticLayersSelector,
  locationV2Selector,
  locationV2MoreOptionsSelector,
  audienceV2FilterPopoverSelector,
  ifReceiverSelector,
  campaignCreatorSelector,
  audienceV2ShowOnlySelectedButtonSelector,
  locationV2ShowOnlySelectedButtonSelector,
  audienceV2CardSelector,
  drawButtonSelector,
  layersPopoverSelector,
  mapDataLayerSelector,
  tackleboxStateForTool,
  tackleboxStateForHistory,
  tackleboxStateForMap
};

// Action creators are generated for each case reducer function
export const {
  addAudience,
  addEgeLayerRequest,
  addLocationFilterCategory,
  addLocationFilterCountry,
  addLocations,
  addWifis,
  replaceWifi,
  clearAllSelections,
  clearAudienceColors,
  clearAudienceDetails,
  clearAudienceDetailsDateRange,
  clearCampaignOnScreenEntities,
  clearFitBoundsMapMarker,
  clearLocationDetails,
  clearLocationFilterCategory,
  clearLocationFilterCountry,
  clearSelectedAudience,
  clearSelectedLocations,
  clearSelectedWifi,
  commitAudienceFilters,
  commitLocationFilters,
  disableDrawTools,
  egeRawToggle,
  enableMagicWand,
  enableMeasurement,
  disableMeasurement,
  fitBoundsComplete,
  fitBoundsOnMap,
  fitBoundsOnMapWithMapMarker,
  flyToOnMap,
  nextDetailsLocation,
  prevDetailsLocation,
  nextDetailsAudience,
  prevDetailsAudience,
  nextDetailsWifi,
  prevDetailsWifi,
  nextDetailsSenpai,
  prevDetailsSenpai,
  clearWifiDetails,
  onMagicWandFinish,
  removeEgeLayerRequest,
  updateEgeLayerRequest,
  renameEgeLayerRequest,
  setAdsbFlights,
  addAdsbFetchedData,
  removeAdsbFetchedData,
  toggleAdsbFetchedData,
  renameAdsbFetchedData,
  toggleAdsbData,
  addAisFetchedData,
  removeAisFetchedData,
  setFetchDateRange,
  setShowFetchDataTimeline,
  toggleAisFetchedData,
  renameAisFetchedData,
  renderEGE,
  replaceAudience,
  replaceLocation,
  restoreSnapshot,
  selectBasemap,
  setSenpaiDetails,
  toggleSenpaiSelected,
  addSenpaiSelected,
  clearSenpaiDetails,
  setSenpaiDetailsActiveTab,
  setAudienceColor,
  setAudienceColors,
  setCampaignEntityColors,
  clearCampaignEntityColors,
  setAudienceDetails,
  setAudienceDetailsActiveTab,
  setSelectedEntityFlightJourneys,
  toggleSelectedEntityFlightJourney,
  addSelectedEntityFlightJourney,
  setSelectedEntityVesselJourneys,
  toggleSelectedEntityVesselJourney,
  addSelectedEntityVesselJourney,
  setEntityFlightJourneyColor,
  setAdsbDetailsActiveTab,
  toggleAdsbDetailCollapsed,
  setAudienceDetailsDateRange,
  setAudienceFilterEntityTypes,
  setAudienceMinPolygons,
  setAudienceMinScore,
  setAudienceSort,
  toggleAudienceSortOrder,
  setCampaign,
  refreshCampaign,
  setCampaignClusterDateRange,
  setCampaignFilters,
  resetCampaignFilters,
  setCampaignH3DwellLayerDate,
  setCampaignH3DwellLayerDim,
  setCampaignH3DwellLayerRes,
  setCampaignH3DwellLayerScaleType,
  setCampaignOnScreenEntities,
  setCampaignRawAggDimension,
  setCampaignRawAggSize,
  setEventCountColorOverride,
  setCampaignSearchText,
  setDelayedMapViewport,
  setMapCoordinateInfo,
  setDrawTool,
  setDrawnBboxBounds,
  addDrawnEgePolygon,
  removeDrawnEgePolygon,
  toggleDrawnEgePolygon,
  clearDrawnEgePolygons,
  setTackleboxAdminLayer,
  setTackleboxDrawnPolygonSelected,
  setTackleboxDrawMode,
  addTackleboxPolygon,
  completeDrawnPolygon,
  setSlotPolygon,
  resetTackleboxParams,
  setTackleboxHistoryTab,
  setTackleboxJobName,
  setTackleboxMinTitoPolygons,
  setTackleboxParams,
  setTackleboxSlots,
  setTackleboxDrawingSlotId,
  setEgeRawDateRange,
  setLocationDetails,
  setLocationDetailsActiveTab,
  setLocationFilterAdvertiserIds,
  setLocationFilterMinUniqueEntities,
  setLocationNetworkPolygonId,
  setLocationSort,
  toggleLocationSortOrder,
  setSenpaiFilterMinRfiCount,
  setSenpaiSort,
  setSearchBarFilter,
  setSelectedDatePreset,
  setSelectedLocationAttrPanel,
  setThemeMode,
  setUser,
  setActivePage,
  setWifiDetails,
  setHoveredWifi,
  setBannerHasFTPOClassification,
  setWifiResults,
  setWifiResultsLoading,
  setLastWifiQueryParams,
  setAriData,
  setAriIsLoading,
  setAriIsError,
  setDataminrData,
  setDataminrIsLoading,
  setDataminrIsError,
  toggleDataminrData,
  setDataminrDetails,
  clearDataminrDetails,
  setDataminrDetailsActiveTab,
  toggleDataminrDetailCollapsed,
  nextDetailsDataminr,
  prevDetailsDataminr,
  toggleDataminrSelected,
  addDataminrSelected,
  clearSelectedDataminr,
  toggleShowOnlySelectedDataminr,
  setDataminrFilterAlertTypes,
  setDataminrSort,
  setWifiSort,
  toggleWifiSortOrder,
  shareCampaign,
  toggleAudience,
  togglePanelHeader,
  openPanelForTab,
  openPanel,
  toggleCampaignCreator,
  togglePanelCampaignCreator,
  closePanelCampaignCreator,
  openPanelFetch,
  closePanelFetch,
  setPanelFetchDataset,
  togglePanelFetchCollapsed,
  toggleCampaignH3DwellLayer,
  setDetailPanelActiveTab,
  toggleCampaignH3DwellLayerScaleFilter,
  toggleCellData,
  enableCellData,
  toggleEgeData,
  toggleEgeLayer,
  toggleLocation,
  toggleLocationFilterCategory,
  toggleLocationFilterCountry,
  toggleLocationNetwork,
  toggleLocationSilent,
  setPolygonMode,
  toggleTouchstoneData,
  enableWifiData,
  enableSenpaiData,
  toggleSenpaiDetections,
  toggleShowOnlySelectedAudience,
  toggleShowOnlySelectedLocations,
  toggleShowOnlySelectedSenpai,
  toggleShowOnlySelectedWifi,
  clearSelectedSenpai,
  toggleWIFIData,
  toggleWifiSelected,
  zoomToWifiPoint,
  setWifiConnectedOrSurveyedData,
  setConnectedOrSurveyedLoading,
  viewCampaignSource,
  toggleOffAllLayers,
  resetMap
} = slice.actions;

export default slice.reducer;
